import { z } from 'zod';

export const PresignedUrlRequestSchema = z.object({
  file_name: z.string(),
  upload_type: z.enum(['Book', 'Product', 'Menu', 'Corner', 'Special', 'Coupon', 'Information', 'Point', 'Loto', 'Incentive'])
});

export const PresignedUrlResponseSchema = z.object({
  upload_url: z.string().optional(),
  upload_id: z.string().optional()
});

export const Iso8601DateTimeSchema = z.string().datetime({ offset: true });

export const UploadRecordSchema = z.object({
  id: z.string().optional(),
  file_name: z.string().optional(),
  upload_type: z.string().optional(),
  status: z.enum(['PENDING', 'PROCESSING', 'SUCCESS', 'PARTIAL_SUCCESS', 'FAILED']).optional(),
  created_at: Iso8601DateTimeSchema.optional(),
  error_message: z.string().optional()
});

export const UserPasswordSchema = z.string().regex(new RegExp("^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[^A-Za-z0-9]).{8,}$")).min(8).max(99);

export const SignupRequestSchema = z.object({
  email: z.string().email(),
  gender: z.string().max(16).optional(),
  birthdate: z.string().regex(new RegExp("^$|^[0-9]{4}-[0-9]{2}-[0-9]{2}$")).optional(),
  magazineStatus: z.boolean().optional()
});

export const RequestPlatformSchema = z.enum(['web', 'ios', 'android']);

export const SignupVerifyRequestSchema = z.object({
  email: z.string().email(),
  code: z.string().regex(new RegExp("^[0-9]{6}$")),
  password: UserPasswordSchema,
  platform: RequestPlatformSchema
});

export const SignupResendRequestSchema = z.object({
  email: z.string().email()
});

export const EmailChangeRequestSchema = z.object({
  new_email: z.string().email(),
  current_password: z.string().min(8).max(99)
});

export const EmailChangeVerifyRequestSchema = z.object({
  code: z.string().regex(new RegExp("^[0-9]{6}$"))
});

export const EmailChangeResendRequestSchema = z.object({

});

export const EmailChangeErrorCodeSchema = z.enum(['VALIDATION_FAILED', 'VALIDATION_FIELD_MISSING', 'VALIDATION_FIELD_INVALID', 'USER_CODE_COOLDOWN', 'USER_PENDING_NOT_FOUND', 'USER_CODE_INVALID', 'USER_CODE_EXPIRED', 'USER_CODE_ATTEMPTS_EXCEEDED', 'AUTH_TOKEN_MISSING', 'AUTH_TOKEN_INVALID', 'AUTH_TOKEN_EXPIRED', 'USER_CURRENT_PASSWORD_INVALID', 'USER_PROFILE_NOT_FOUND', 'USER_EMAIL_ALREADY_REGISTERED', 'USER_AUTH0_RATE_LIMITED', 'CONFIG_MISSING', 'INTERNAL_UNEXPECTED_ERROR']);

export const EmailChangeErrorResponseSchema = z.object({
  errorCode: EmailChangeErrorCodeSchema,
  detail: z.string(),
  status: z.number().int(),
  isRetryable: z.boolean()
});

export const EmailChangeUnauthorizedResponseSchema = z.object({
  message: z.string().optional(),
  errorCode: EmailChangeErrorCodeSchema.optional(),
  detail: z.string().optional(),
  status: z.number().int().optional()
});

export const VerificationCodeSchema = z.string().regex(new RegExp("^[0-9]{6}$"));

export const PasswordChangeRequestSchema = z.object({
  current_password: z.string().min(8).max(99),
  new_password: UserPasswordSchema,
  new_password_confirm: UserPasswordSchema,
  code: VerificationCodeSchema
});

export const PasswordChangeCodeRequestSchema = z.object({

});

export const PasswordChangeCodeResendRequestSchema = z.object({

});

export const PasswordChangeCodeErrorCodeSchema = z.enum(['VALIDATION_FAILED', 'VALIDATION_FIELD_INVALID', 'USER_CODE_COOLDOWN', 'USER_PENDING_NOT_FOUND', 'USER_CODE_EXPIRED', 'USER_CODE_ATTEMPTS_EXCEEDED', 'AUTH_TOKEN_INVALID', 'USER_PROFILE_NOT_FOUND', 'CONFIG_MISSING', 'INTERNAL_UNEXPECTED_ERROR']);

export const PasswordChangeCodeErrorResponseSchema = z.object({
  errorCode: PasswordChangeCodeErrorCodeSchema,
  detail: z.string(),
  status: z.number().int(),
  isRetryable: z.boolean()
});

export const MessageResponseSchema = z.object({
  message: z.string()
});

export const UserEmailSchema = z.string().email();

export const UserPasswordSecretSchema = z.string().regex(new RegExp("^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[^A-Za-z0-9]).{8,}$")).min(8).max(99);

export const PasswordResetRequestSchema = z.object({
  email: UserEmailSchema
});

export const PasswordResetVerifyRequestSchema = z.object({
  email: UserEmailSchema,
  code: VerificationCodeSchema,
  new_password: UserPasswordSecretSchema,
  new_password_confirm: UserPasswordSecretSchema
});

export const PasswordResetResendRequestSchema = z.object({
  email: UserEmailSchema
});

export const PasswordResetErrorCodeSchema = z.enum(['VALIDATION_FAILED', 'USER_EMAIL_MISSING', 'USER_PENDING_NOT_FOUND', 'USER_CODE_INVALID', 'USER_CODE_EXPIRED', 'USER_CODE_ATTEMPTS_EXCEEDED', 'USER_CODE_COOLDOWN', 'USER_PASSWORD_MISMATCH', 'USER_PASSWORD_POLICY', 'USER_AUTH0_ERROR', 'CONFIG_MISSING', 'INTERNAL_UNEXPECTED_ERROR']);

export const PasswordResetErrorResponseSchema = z.object({
  errorCode: PasswordResetErrorCodeSchema,
  detail: z.string(),
  status: z.number().int().optional()
});

export const DeviceIdSchema = z.string().min(1).max(255);

export const ClientPlatformSchema = z.enum(['web', 'ios', 'android']);

export const SigninRequestSchema = z.object({
  email: UserEmailSchema,
  password: z.string().min(1).max(99),
  device_id: DeviceIdSchema.optional(),
  device_name: z.string().max(255).optional(),
  platform: ClientPlatformSchema
});

export const UserSchema = z.object({
  sub: z.string(),
  email: UserEmailSchema,
  name: z.string().optional(),
  picture: z.string().optional()
});

export const SessionSchema = z.object({
  access_token: z.string(),
  id_token: z.string().optional(),
  refresh_token: z.string().optional(),
  token_type: z.string(),
  expires_at: z.string(),
  device_session_id: z.string().optional()
});

export const SigninResponseSchema = z.object({
  user: UserSchema,
  session: SessionSchema
});

export const RefreshRequestSchema = z.object({
  refresh_token: z.string().min(1)
});

export const RefreshResponseSchema = z.object({
  session: SessionSchema
});

export const LogoutRequestSchema = z.object({
  refresh_token: z.string().min(1).optional()
});

export const DeviceItemSchema = z.object({
  device_id: DeviceIdSchema,
  device_name: z.string(),
  registered_at: z.string(),
  last_access_at: z.string(),
  is_current: z.boolean()
});

export const DeviceListResponseSchema = z.object({
  devices: z.array(DeviceItemSchema)
});

export const DeviceSessionValidResponseSchema = z.object({
  valid: z.boolean()
});

export const SessionErrorCodeSchema = z.enum(['VALIDATION_FAILED', 'VALIDATION_FIELD_INVALID', 'VALIDATION_FIELD_MISSING', 'USER_INVALID_CREDENTIALS', 'USER_DEVICE_LIMIT_EXCEEDED', 'USER_CURRENT_DEVICE_DELETE_FORBIDDEN', 'USER_SESSION_EXPIRED', 'USER_SESSION_UNVERIFIABLE', 'USER_PROFILE_NOT_FOUND', 'USER_AUTH0_RATE_LIMITED', 'CONFIG_MISSING', 'INTERNAL_UNEXPECTED_ERROR']);

export const SessionErrorResponseSchema = z.object({
  errorCode: SessionErrorCodeSchema,
  detail: z.string(),
  status: z.number().int().optional(),
  isRetryable: z.boolean().optional()
});

export const PasswordChangeErrorCodeSchema = z.enum(['VALIDATION_FAILED', 'VALIDATION_FIELD_MISSING', 'VALIDATION_FIELD_INVALID', 'USER_CURRENT_PASSWORD_INVALID', 'USER_PENDING_NOT_FOUND', 'USER_CODE_INVALID', 'USER_CODE_EXPIRED', 'USER_CODE_ATTEMPTS_EXCEEDED', 'USER_SAME_PASSWORD', 'USER_PASSWORD_MISMATCH', 'USER_AUTH0_ERROR', 'AUTH_TOKEN_INVALID', 'USER_PROFILE_NOT_FOUND', 'USER_PASSWORD_POLICY', 'CONFIG_MISSING', 'INTERNAL_UNEXPECTED_ERROR', 'USER_AUTH0_RATE_LIMITED']);

export const PasswordChangeErrorResponseSchema = z.object({
  errorCode: PasswordChangeErrorCodeSchema,
  detail: z.string(),
  status: z.number().int(),
  isRetryable: z.boolean()
});

export const WithdrawErrorCodeSchema = z.enum(['USER_PROFILE_NOT_FOUND', 'USER_AUTH0_RATE_LIMITED', 'CONFIG_MISSING', 'INTERNAL_UNEXPECTED_ERROR']);

export const WithdrawErrorResponseSchema = z.object({
  errorCode: WithdrawErrorCodeSchema,
  detail: z.string(),
  status: z.number().int(),
  isRetryable: z.boolean()
});

export const WithdrawUnauthorizedResponseSchema = z.object({
  message: z.string().optional()
});

export const MagazineStatusResponseSchema = z.object({
  magazine_status: z.boolean()
});

export const MagazineStatusUpdateRequestSchema = z.object({
  magazine_status: z.boolean()
});

export const MagazineStatusErrorCodeSchema = z.enum(['VALIDATION_FIELD_MISSING', 'VALIDATION_FIELD_INVALID', 'VALIDATION_FAILED', 'AUTH_TOKEN_INVALID', 'USER_SESSION_EXPIRED', 'USER_PROFILE_NOT_FOUND', 'CONFIG_MISSING', 'INTERNAL_UNEXPECTED_ERROR', 'INTERNAL_DATABASE_ERROR']);

export const MagazineStatusErrorResponseSchema = z.object({
  errorCode: MagazineStatusErrorCodeSchema,
  detail: z.string(),
  status: z.number().int(),
  isRetryable: z.boolean()
});

export const MagazineStatusUnauthorizedResponseSchema = z.object({
  message: z.string().optional(),
  errorCode: MagazineStatusErrorCodeSchema.optional(),
  detail: z.string().optional(),
  status: z.number().int().optional()
});

export const JdcnSchema = z.string().regex(new RegExp("^[a-zA-Z0-9]{20}$"));

export const CouponIdSchema = z.string().regex(new RegExp("^[a-zA-Z0-9_-]{32}$"));

export const AlphanumericSlugSchema = z.string().regex(new RegExp("^[a-zA-Z0-9_\\-]+$"));

export const PlatformSchema = z.string().regex(new RegExp("^[0-2]{3}$"));

export const PublisherSchema = z.enum(['白泉社', '集英社']);

export const ContentTypeSchema = z.enum(['comic', 'novel', 'comic_magazine', 'novel_magazine', 'koma', 'comic_girl', 'comic_woman', 'comic_man', 'comic_boy', 'comic_BL', 'comic_gravure', 'comic_picturebook', 'comic_magazine_girl', 'comic_magazine_woman', 'comic_magazine_man', 'comic_magazine_boy', 'comic_magazine_BL', 'novel_BL', 'novel_woman', 'novel_man', 'novel_maneki', 'novel_general', 'novel_magazine_BL', 'novel_magazine_woman', 'novel_magazine_man', 'novel_magazine_general', 'novel_magazine_yonetsu', 'comic_tate_girl', 'comic_tate_woman', 'comic_tate_man', 'comic_tate_boy', 'comic_tate_BL']);

export const ProductContentTypeSchema = z.enum(['comic', 'novel', 'comic_magazine', 'novel_magazine']);

export const AuthorSchema = z.object({
  name: z.string().min(1),
  kana: z.string().min(1),
  title: z.string().min(1)
});

export const CsvDateTimeSchema = z.string().regex(new RegExp("^(\\d{4}[/-]\\d{1,2}[/-]\\d{1,2}) \\d{1,2}:\\d{2}(:\\d{2})?$"));

export const CsvDateSchema = z.string().regex(new RegExp("^(\\d{4}[/-]\\d{1,2}[/-]\\d{1,2})( \\d{1,2}:\\d{2}(:\\d{2})?)?$"));

export const PublishSinceSchema = z.number().int().min(1900).max(2100);

export const PriceSchema = z.number().int().min(0);

export const IntroductionSchema = z.string().min(1);

export const LabelSchema = z.string().min(1);

export const CoverImageSchema = z.string();

export const CategoryCsvSchema = z.string().regex(new RegExp("^(\\d+(,\\d+)*)?$"));

export const CategoryMetaSchema = z.object({
  name: z.string().min(1),
  title: z.string().min(1)
});

export const SearchKeywordsSchema = z.string().regex(new RegExp("^.{0,500}$"));

export const HasCollectionSchema = z.enum(['true', '1', '0', '']);

export const HasCollectionBooleanSchema = z.boolean();

export const ValidityStatusSchema = z.enum(['valid', 'invalid']);

export const ProductTypeSchema = z.enum(['manage', 'custom']);

export const CampaignPriceTypeSchema = z.enum(['normal', 'point', 'percent']);

export const SeriesStatusSchema = z.enum(['complete', 'ongoing']);

export const PlatformStatusSchema = z.enum(['hidden', 'view_only', 'sale']);

export const PlatformDetailSchema = z.object({
  web: PlatformStatusSchema,
  ios: PlatformStatusSchema,
  android: PlatformStatusSchema
});

export const BookBaseSchema = z.object({
  jdcn: JdcnSchema.optional(),
  title_name: AlphanumericSlugSchema.optional(),
  title_title: z.string().min(1).optional(),
  title_kana: z.string().min(1).optional(),
  volume_name: AlphanumericSlugSchema.optional(),
  volume_title: z.string().min(1).optional(),
  volume_kana: z.string().min(1).optional(),
  volume_number_title: z.string().regex(new RegExp("^.{0,100}$")).optional(),
  is_completed: z.boolean().optional(),
  file_name: AlphanumericSlugSchema.optional(),
  magazine_series_name: AlphanumericSlugSchema.optional(),
  magazine_series_title: z.string().min(1).optional(),
  magazine_name: AlphanumericSlugSchema.optional(),
  magazine_title: z.string().min(1).optional(),
  book_type: ContentTypeSchema.optional(),
  base_price: PriceSchema.optional(),
  introduction: IntroductionSchema.optional(),
  label: LabelSchema.optional(),
  publish_since: PublishSinceSchema.optional(),
  publisher: PublisherSchema.optional(),
  platform: PlatformSchema.optional(),
  isbn: z.string().regex(new RegExp("^[0-9\\-]{10,17}$")).optional(),
  jasrac: z.string().regex(new RegExp("^.{0,200}$")).optional(),
  search_keywords: SearchKeywordsSchema.optional()
});

export const BookCsvUploadSchema = z.object({
  jdcn: JdcnSchema,
  title_name: AlphanumericSlugSchema,
  title_title: z.string().min(1),
  title_kana: z.string().min(1),
  volume_name: AlphanumericSlugSchema,
  volume_title: z.string().min(1),
  volume_kana: z.string().min(1),
  volume_number_title: z.string().regex(new RegExp("^.{0,100}$")).optional(),
  is_completed: z.boolean().optional(),
  file_name: AlphanumericSlugSchema,
  magazine_series_name: AlphanumericSlugSchema,
  magazine_series_title: z.string().min(1),
  magazine_name: AlphanumericSlugSchema,
  magazine_title: z.string().min(1),
  book_type: ContentTypeSchema,
  base_price: PriceSchema,
  introduction: IntroductionSchema,
  label: LabelSchema,
  publish_since: PublishSinceSchema,
  publisher: PublisherSchema,
  platform: PlatformSchema,
  isbn: z.string().regex(new RegExp("^[0-9\\-]{10,17}$")).optional(),
  jasrac: z.string().regex(new RegExp("^.{0,200}$")).optional(),
  search_keywords: SearchKeywordsSchema.optional(),
  volume_number: z.string().min(1),
  author_name: z.string().min(1),
  author_kana: z.string().min(1),
  author_title: z.string().min(1),
  has_collection: HasCollectionSchema.optional(),
  collection_source_jdcn: JdcnSchema.optional(),
  first_publish_at: CsvDateSchema,
  release_at: CsvDateTimeSchema,
  publish_at: CsvDateTimeSchema,
  stop_at: CsvDateTimeSchema.optional(),
  trial_file_name: AlphanumericSlugSchema,
  trial_pages: z.number().int().min(0).optional(),
  category: CategoryCsvSchema.optional()
});

export const BookDbSchema = z.object({
  jdcn: JdcnSchema,
  title_name: AlphanumericSlugSchema.optional(),
  title_title: z.string().min(1).optional(),
  title_kana: z.string().min(1).optional(),
  volume_name: AlphanumericSlugSchema.optional(),
  volume_title: z.string().min(1).optional(),
  volume_kana: z.string().min(1).optional(),
  volume_number_title: z.string().regex(new RegExp("^.{0,100}$")).optional(),
  is_completed: z.boolean().optional(),
  file_name: AlphanumericSlugSchema.optional(),
  magazine_series_name: AlphanumericSlugSchema.optional(),
  magazine_series_title: z.string().optional(),
  magazine_name: AlphanumericSlugSchema.optional(),
  magazine_title: z.string().min(1).optional(),
  book_type: ContentTypeSchema.optional(),
  base_price: PriceSchema.optional(),
  introduction: IntroductionSchema.optional(),
  label: LabelSchema.optional(),
  publish_since: PublishSinceSchema.optional(),
  publisher: PublisherSchema.optional(),
  platform: PlatformDetailSchema.optional(),
  isbn: z.string().regex(new RegExp("^[0-9\\-]{10,17}$")).optional(),
  jasrac: z.string().regex(new RegExp("^.{0,200}$")).optional(),
  search_keywords: SearchKeywordsSchema.optional(),
  id: z.string(),
  meta: z.string().optional(),
  volume_number: z.number().optional(),
  author: z.array(AuthorSchema).optional(),
  trial_file: z.string().optional(),
  trial_pages: z.number().int().optional(),
  view_type: z.enum(['FIX', 'OMF', 'RFL']).optional(),
  has_collection: HasCollectionBooleanSchema.optional(),
  collection_source_jdcn: JdcnSchema.optional(),
  category: z.array(z.string()).optional(),
  keyword: z.string().optional(),
  publish_at: Iso8601DateTimeSchema.optional(),
  release_at: Iso8601DateTimeSchema.optional(),
  stop_at: Iso8601DateTimeSchema.optional(),
  first_release_at: Iso8601DateTimeSchema.optional(),
  status: z.enum(['scheduled', 'published']).optional()
});

export const TitleDbSchema = z.object({
  id: z.string(),
  data_type: z.string(),
  title_title: z.string().min(1),
  title_kana: z.string().optional(),
  title_order_kana: z.string().optional(),
  magazine_series_name: z.string().optional(),
  magazine_series_title: z.string().optional(),
  magazine_name: z.string().optional(),
  magazine_title: z.string().optional(),
  author: z.array(AuthorSchema).optional(),
  publisher: PublisherSchema.optional(),
  introduction: IntroductionSchema.optional(),
  publish_since: PublishSinceSchema.optional(),
  latest_volume: z.number().optional(),
  latest_release_at: Iso8601DateTimeSchema.optional(),
  latest_file_name: z.string().optional(),
  platform: PlatformDetailSchema.optional(),
  series_status: SeriesStatusSchema.optional()
});

export const MagazineDbSchema = z.object({
  id: z.string(),
  data_type: z.string(),
  title_title: z.string().min(1),
  magazine_name: z.string().optional(),
  magazine_series_title: z.string().optional(),
  latest_release_at: Iso8601DateTimeSchema.optional(),
  latest_file_name: z.string().optional(),
  platform: PlatformDetailSchema.optional(),
  magazine_catch: z.string().optional()
});

export const BookshelfSortSchema = z.enum(['purchased_at', 'title', 'author', 'publisher', 'magazine']);

export const ReadStatusSchema = z.enum(['unread', 'reading', 'completed']);

export const BookshelfSortRanksSchema = z.object({
  purchased_at: z.number().int().min(0),
  title: z.number().int().min(0),
  author: z.number().int().min(0),
  publisher: z.number().int().min(0),
  magazine: z.number().int().min(0)
});

export const BookshelfSortSectionsSchema = z.object({
  purchased_at: z.string(),
  title: z.string(),
  author: z.string(),
  publisher: z.string(),
  magazine: z.string()
});

export const BookshelfRecentVolumeSchema = z.object({
  jdcn: JdcnSchema,
  title_name: AlphanumericSlugSchema,
  title_title: z.string().min(1),
  volume_number: z.number().int().min(0).optional(),
  volume_number_title: z.string().optional(),
  cover_image: CoverImageSchema.optional(),
  author: z.array(z.string().min(1)).optional(),
  book_type: ContentTypeSchema.optional(),
  read_status: ReadStatusSchema,
  last_read_at: Iso8601DateTimeSchema
});

export const BookshelfTitleItemSchema = z.object({
  title_name: AlphanumericSlugSchema,
  title_title: z.string().min(1),
  author: z.array(z.string().min(1)).optional(),
  publisher: PublisherSchema.optional(),
  magazine_series_title: z.string().optional(),
  cover_image: CoverImageSchema.optional(),
  owned_count: z.number().int().min(1),
  unread_count: z.number().int().min(0),
  purchased_at: Iso8601DateTimeSchema,
  hidden: z.boolean(),
  reshown: z.boolean().optional(),
  order: BookshelfSortRanksSchema,
  section: BookshelfSortSectionsSchema,
  search_text: z.string()
});

export const BookshelfResponseSchema = z.object({
  recent_volumes: z.array(BookshelfRecentVolumeSchema).max(100),
  titles: z.array(BookshelfTitleItemSchema),
  next_cursor: z.string().optional(),
  generated_at: Iso8601DateTimeSchema
});

export const BookshelfVolumeTitleSchema = z.object({
  title_name: AlphanumericSlugSchema,
  title_title: z.string().min(1),
  author: z.array(z.string().min(1)).optional(),
  hidden: z.boolean()
});

export const ProductListItemSchema = z.object({
  jdcn: JdcnSchema.optional(),
  title_name: AlphanumericSlugSchema.optional(),
  title_title: z.string().min(1).optional(),
  volume_title: z.string().min(1).optional(),
  volume_number_title: z.string().optional(),
  product_order: z.number().int().min(0).optional(),
  is_free_streaming: z.boolean().optional(),
  introduction: IntroductionSchema.optional(),
  cover_image: CoverImageSchema.optional(),
  author: z.array(z.string().min(1)).optional(),
  magazine_series_name: AlphanumericSlugSchema.optional(),
  magazine_series_title: z.string().min(1).optional(),
  magazine_name: AlphanumericSlugSchema.optional(),
  magazine_title: z.string().min(1).optional(),
  category: z.array(CategoryMetaSchema).optional(),
  taxed_sale_price: PriceSchema.optional(),
  taxed_base_price: PriceSchema.optional(),
  release_at: Iso8601DateTimeSchema.optional(),
  product_catch: z.string().optional(),
  sale_status: z.enum(['sale', 'view_only']).optional()
});

export const BookshelfVolumeItemSchema = z.object({
  jdcn: JdcnSchema,
  volume_number: z.number().int().min(0).optional(),
  volume_number_title: z.string().optional(),
  cover_image: CoverImageSchema.optional(),
  owned: z.boolean(),
  read_status: ReadStatusSchema.optional(),
  book_type: ContentTypeSchema.optional(),
  product: ProductListItemSchema.optional()
});

export const BookshelfVolumesResponseSchema = z.object({
  title: BookshelfVolumeTitleSchema,
  default_order: z.enum(['asc', 'desc']),
  unowned_unavailable: z.boolean(),
  volumes: z.array(BookshelfVolumeItemSchema)
});

export const BookshelfSearchResponseSchema = z.object({
  title_names: z.array(AlphanumericSlugSchema).max(1000),
  truncated: z.boolean(),
  degraded: z.boolean()
});

export const ReadingStateRequestSchema = z.object({
  completed: z.boolean(),
  read_at: Iso8601DateTimeSchema,
  position: z.string().max(512).optional()
});

export const ReadingStateUpdateResponseSchema = z.object({
  jdcn: JdcnSchema,
  read_status: ReadStatusSchema,
  last_read_at: Iso8601DateTimeSchema,
  applied: z.boolean()
});

export const ReadingStateResponseSchema = z.object({
  jdcn: JdcnSchema,
  read_status: ReadStatusSchema,
  position: z.string().max(512).optional(),
  last_read_at: Iso8601DateTimeSchema.optional()
});

export const BookshelfVisibilityItemSchema = z.object({
  title_name: AlphanumericSlugSchema,
  hidden: z.boolean()
});

export const BookshelfVisibilityRequestSchema = z.object({
  as_of: Iso8601DateTimeSchema,
  items: z.array(BookshelfVisibilityItemSchema).min(1).max(1000)
});

export const BookshelfVisibilityResultItemSchema = z.object({
  title_name: AlphanumericSlugSchema,
  applied: z.boolean(),
  hidden: z.boolean().optional()
});

export const BookshelfVisibilityResponseSchema = z.object({
  as_of_next: Iso8601DateTimeSchema,
  items: z.array(BookshelfVisibilityResultItemSchema)
});

export const BookshelfErrorCodeSchema = z.enum(['VALIDATION_FAILED', 'VALIDATION_FIELD_MISSING', 'VALIDATION_FIELD_INVALID', 'PLATFORM_PARAM_INVALID', 'AUTH_TOKEN_MISSING', 'AUTH_TOKEN_INVALID', 'AUTH_TOKEN_EXPIRED', 'USER_PROFILE_NOT_FOUND', 'BOOKSHELF_TITLE_NOT_OWNED', 'BOOKSHELF_VOLUME_NOT_OWNED', 'BOOKSHELF_HIDDEN_LIMIT_EXCEEDED', 'BOOKSHELF_VISIBILITY_STALE', 'BOOKSHELF_VISIBILITY_PARTIALLY_FAILED', 'CONFIG_MISSING', 'INTERNAL_DATABASE_ERROR', 'INTERNAL_UNEXPECTED_ERROR']);

export const BookshelfErrorContextSchema = z.object({
  applied_title_names: z.array(AlphanumericSlugSchema).optional(),
  failed_title_names: z.array(AlphanumericSlugSchema).optional(),
  as_of_next: Iso8601DateTimeSchema.optional()
});

export const BookshelfErrorResponseSchema = z.object({
  errorCode: BookshelfErrorCodeSchema,
  detail: z.string(),
  status: z.number().int(),
  isRetryable: z.boolean(),
  context: BookshelfErrorContextSchema.optional()
});

export const CartTypeSchema = z.enum(['cart', 'later_purchase']);

export const CartItemSchema = z.object({
  product_name: JdcnSchema,
  title_name: AlphanumericSlugSchema.optional(),
  title_title: z.string().min(1),
  magazine_name: AlphanumericSlugSchema.optional(),
  volume_title: z.string().min(1),
  cover_image: CoverImageSchema,
  author: z.array(z.string().min(1)),
  taxed_base_price: PriceSchema,
  taxed_sale_price: PriceSchema,
  product_catch: z.string().optional(),
  platform: PlatformDetailSchema
});

export const CartItemsResponseSchema = z.object({
  cart: z.array(CartItemSchema),
  later_purchase: z.array(CartItemSchema)
});

export const CartItemsRequestSchema = z.object({
  type: CartTypeSchema,
  product_names: z.array(JdcnSchema).min(1).max(300)
});

export const CartCountResponseSchema = z.object({
  cart_count: z.number().int().min(0),
  later_count: z.number().int().min(0)
});

export const CartStateResponseSchema = z.object({
  cart_count: z.number().int().min(0),
  cart_product_names: z.array(JdcnSchema)
});

export const CartErrorCodeSchema = z.enum(['VALIDATION_FAILED', 'VALIDATION_FIELD_MISSING', 'VALIDATION_FIELD_INVALID', 'AUTH_TOKEN_INVALID', 'USER_PROFILE_NOT_FOUND', 'CART_LIMIT_EXCEEDED', 'CONFIG_MISSING', 'INTERNAL_DATABASE_ERROR', 'INTERNAL_UNEXPECTED_ERROR']);

export const CartErrorResponseSchema = z.object({
  errorCode: CartErrorCodeSchema,
  detail: z.string(),
  status: z.number().int().optional()
});

export const CornerPlatformSchema = z.object({
  web: z.boolean(),
  ios: z.boolean(),
  android: z.boolean()
});

export const CornerBlockTypeSchema = z.enum(['slide', 'auto_slide', 'static', 'static_1520b', 'table_list']);

export const CornerGenerateTypeSchema = z.enum(['auto', 'manual']);

export const CornerItemTypeSchema = z.enum(['banner', 'product', 'image', 'external']);

export const CornerCsvDataTypeSchema = z.enum(['PAGE', 'BLOCK', 'ITEM']);

export const CornerCsvUploadSchema = z.object({
  data_type: CornerCsvDataTypeSchema,
  corner_name: z.string().optional(),
  slug: AlphanumericSlugSchema.optional(),
  block_id: AlphanumericSlugSchema.optional(),
  start_at: CsvDateTimeSchema.optional(),
  stop_at: CsvDateTimeSchema.optional(),
  title: z.string().optional(),
  block_type: CornerBlockTypeSchema.optional(),
  generate_type: CornerGenerateTypeSchema.optional(),
  block_link_to: z.string().optional(),
  block_link_target: z.string().optional(),
  block_link_txt: z.string().optional(),
  item_type: CornerItemTypeSchema.optional(),
  item: z.string().optional(),
  item_link_to: z.string().optional(),
  item_link_target: z.string().optional(),
  platform: z.string().regex(new RegExp("^[01]{3}$")).optional(),
  note: z.string().optional()
});

export const CornerProductItemSchema = z.object({
  jdcn: JdcnSchema,
  cover_image: CoverImageSchema,
  volume_title: z.string().min(1),
  title_title: z.string().optional(),
  volume_number_title: z.string().optional(),
  author: z.array(z.string().min(1)).optional(),
  is_free_streaming: z.boolean().optional()
});

export const CornerItemBaseSchema = z.object({
  item_type: CornerItemTypeSchema,
  item: z.string().min(1),
  link_to: z.string().optional(),
  link_to_target: z.string().optional(),
  title: z.string().optional()
});

export const CornerItemSchema = z.object({
  item_type: CornerItemTypeSchema,
  item: z.string().min(1),
  link_to: z.string().optional(),
  link_to_target: z.string().optional(),
  title: z.string().optional(),
  platform: CornerPlatformSchema.optional(),
  item_start_at: Iso8601DateTimeSchema.optional(),
  item_stop_at: Iso8601DateTimeSchema.optional()
});

export const CornerResponseItemSchema = z.object({
  item_type: CornerItemTypeSchema,
  item: z.string().min(1),
  link_to: z.string().optional(),
  link_to_target: z.string().optional(),
  title: z.string().optional(),
  product: CornerProductItemSchema.optional()
});

export const CornerBlockBaseSchema = z.object({
  block_id: AlphanumericSlugSchema,
  block_type: CornerBlockTypeSchema,
  generate_type: CornerGenerateTypeSchema,
  title: z.string().optional(),
  block_start_at: Iso8601DateTimeSchema.optional(),
  block_stop_at: Iso8601DateTimeSchema.optional(),
  link_to: z.string().optional(),
  link_to_target: z.string().optional(),
  link_to_txt: z.string().optional()
});

export const CornerResponseBlockSchema = z.object({
  block_id: AlphanumericSlugSchema,
  block_type: CornerBlockTypeSchema,
  generate_type: CornerGenerateTypeSchema,
  title: z.string().optional(),
  block_start_at: Iso8601DateTimeSchema.optional(),
  block_stop_at: Iso8601DateTimeSchema.optional(),
  link_to: z.string().optional(),
  link_to_target: z.string().optional(),
  link_to_txt: z.string().optional(),
  items: z.array(CornerResponseItemSchema).optional()
});

export const CornerBlockSchema = z.object({
  block_id: AlphanumericSlugSchema,
  block_type: CornerBlockTypeSchema,
  generate_type: CornerGenerateTypeSchema,
  title: z.string().optional(),
  block_start_at: Iso8601DateTimeSchema.optional(),
  block_stop_at: Iso8601DateTimeSchema.optional(),
  link_to: z.string().optional(),
  link_to_target: z.string().optional(),
  link_to_txt: z.string().optional(),
  platform: CornerPlatformSchema,
  items: z.array(CornerItemSchema).optional()
});

export const CornerSchema = z.object({
  corner_id: z.string().min(1),
  slug: AlphanumericSlugSchema,
  start_at: Iso8601DateTimeSchema,
  stop_at: Iso8601DateTimeSchema.optional(),
  title: z.string().optional(),
  blocks: z.array(CornerBlockSchema).min(1),
  updated_at: Iso8601DateTimeSchema.optional()
});

export const CornerDbSchema = z.object({
  id: z.string(),
  data_type: z.string(),
  corner_id: z.string().min(1),
  slug: AlphanumericSlugSchema,
  title: z.string().optional(),
  start_at: Iso8601DateTimeSchema,
  stop_at: Iso8601DateTimeSchema.optional(),
  note: z.string().optional(),
  ttl_timestamp: z.number().int().optional(),
  updated_at: Iso8601DateTimeSchema.optional()
});

export const BlockDbSchema = z.object({
  block_id: AlphanumericSlugSchema,
  block_type: CornerBlockTypeSchema,
  generate_type: CornerGenerateTypeSchema,
  title: z.string().optional(),
  block_start_at: Iso8601DateTimeSchema.optional(),
  block_stop_at: Iso8601DateTimeSchema.optional(),
  link_to: z.string().optional(),
  link_to_target: z.string().optional(),
  link_to_txt: z.string().optional(),
  id: z.string(),
  corner_ref: z.string(),
  sort_path: z.string(),
  slug: AlphanumericSlugSchema,
  block_order: z.number().int().min(0),
  platform: CornerPlatformSchema,
  view_limit: z.number().int().min(0).optional(),
  see_more_enabled: z.boolean().optional(),
  note: z.string().optional(),
  ttl_timestamp: z.number().int().optional()
});

export const ItemDbSchema = z.object({
  item_type: CornerItemTypeSchema,
  item: z.string().min(1),
  link_to: z.string().optional(),
  link_to_target: z.string().optional(),
  title: z.string().optional(),
  id: z.string(),
  corner_ref: z.string(),
  sort_path: z.string(),
  block_ref: z.string(),
  item_order: z.number().int().min(0),
  slug: AlphanumericSlugSchema,
  block_id: AlphanumericSlugSchema,
  platform: CornerPlatformSchema.optional(),
  item_start_at: Iso8601DateTimeSchema.optional(),
  item_stop_at: Iso8601DateTimeSchema.optional(),
  ttl_timestamp: z.number().int().optional()
});

export const CornerResponseSchema = z.object({
  slug: AlphanumericSlugSchema.optional(),
  blocks: z.array(CornerResponseBlockSchema)
});

export const CouponGetTypeSchema = z.enum(['auto_grant', 'user_claim']);

export const CouponUseTypeSchema = z.enum(['all', 'group', 'product']);

export const CouponUseTargetSchema = z.object({
  name: z.string(),
  title: z.string()
});

export const CouponDiscountTypeSchema = z.enum(['percent', 'fixed']);

export const CouponUsableCountSchema = z.union([z.number().int().min(1), z.enum(['-1'])]);

export const CouponEntityTypeSchema = z.enum(['CouponDefinition', 'CouponScope', 'CouponClaim', 'UserCoupon', 'CouponUsage']);

export const OwnedCouponStatusSchema = z.enum(['available', 'before_start', 'used', 'expired']);

export const CouponCsvUploadSchema = z.object({
  coupon_id: CouponIdSchema,
  title: z.string().min(1),
  coupon_description: z.string().min(1),
  start_at: CsvDateTimeSchema,
  end_at: CsvDateTimeSchema.optional(),
  get_type: CouponGetTypeSchema,
  get_from_list: z.string().regex(new RegExp("^[01]$")).optional(),
  use_type: CouponUseTypeSchema,
  use_list: z.string().optional(),
  discount_type: CouponDiscountTypeSchema,
  value: z.number().int().min(1),
  usable_period: z.number().int().min(1).optional(),
  usable_start_at: CsvDateTimeSchema.optional(),
  usable_end_at: CsvDateTimeSchema.optional(),
  usable_count: CouponUsableCountSchema,
  is_combine_usable: z.string().regex(new RegExp("^[01]$")).optional(),
  buy_now_only: z.string().regex(new RegExp("^[01]$")).optional(),
  buy_cart_only: z.string().regex(new RegExp("^[01]$")).optional()
});

export const CouponDbSchema = z.object({
  PK: z.string(),
  SK: z.string(),
  entity_type: CouponEntityTypeSchema,
  coupon_id: CouponIdSchema,
  title: z.string().min(1),
  coupon_description: z.string().min(1),
  start_at: Iso8601DateTimeSchema,
  end_at: Iso8601DateTimeSchema.optional(),
  get_type: CouponGetTypeSchema,
  get_from_list: z.boolean().optional(),
  use_type: CouponUseTypeSchema,
  use_list: z.array(z.string()).optional(),
  discount_type: CouponDiscountTypeSchema,
  value: z.number().int().min(1),
  usable_period: z.number().int().min(1).optional(),
  usable_start_at: Iso8601DateTimeSchema.optional(),
  usable_end_at: Iso8601DateTimeSchema.optional(),
  usable_count: CouponUsableCountSchema,
  is_combine_usable: z.boolean().optional(),
  buy_now_only: z.boolean().optional(),
  buy_cart_only: z.boolean().optional(),
  GSI2PK: z.string().optional(),
  GSI2SK: z.string().optional(),
  created_at: Iso8601DateTimeSchema,
  updated_at: Iso8601DateTimeSchema
});

export const CouponScopeDbSchema = z.object({
  PK: z.string(),
  SK: z.string(),
  entity_type: CouponEntityTypeSchema,
  coupon_id: CouponIdSchema,
  get_type: CouponGetTypeSchema,
  start_at: Iso8601DateTimeSchema,
  end_at: Iso8601DateTimeSchema.optional()
});

export const CouponDefinitionPublicSchema = z.object({
  coupon_id: CouponIdSchema,
  title: z.string().min(1),
  coupon_description: z.string().min(1),
  start_at: Iso8601DateTimeSchema,
  end_at: Iso8601DateTimeSchema.optional(),
  get_type: CouponGetTypeSchema,
  use_type: CouponUseTypeSchema,
  use_list: z.array(z.string()).optional(),
  use_targets: z.array(CouponUseTargetSchema).optional(),
  discount_type: CouponDiscountTypeSchema,
  value: z.number().int(),
  usable_period: z.number().int().optional(),
  usable_start_at: Iso8601DateTimeSchema.optional(),
  usable_end_at: Iso8601DateTimeSchema.optional(),
  usable_count: CouponUsableCountSchema,
  is_combine_usable: z.boolean(),
  buy_now_only: z.boolean(),
  buy_cart_only: z.boolean()
});

export const ClaimableCouponItemSchema = z.object({
  coupon_id: CouponIdSchema,
  title: z.string().min(1),
  coupon_description: z.string().min(1),
  start_at: Iso8601DateTimeSchema,
  end_at: Iso8601DateTimeSchema.optional(),
  get_type: CouponGetTypeSchema,
  use_type: CouponUseTypeSchema,
  use_list: z.array(z.string()).optional(),
  use_targets: z.array(CouponUseTargetSchema).optional(),
  discount_type: CouponDiscountTypeSchema,
  value: z.number().int(),
  usable_period: z.number().int().optional(),
  usable_start_at: Iso8601DateTimeSchema.optional(),
  usable_end_at: Iso8601DateTimeSchema.optional(),
  usable_count: CouponUsableCountSchema,
  is_combine_usable: z.boolean(),
  buy_now_only: z.boolean(),
  buy_cart_only: z.boolean(),
  claimed: z.boolean()
});

export const OwnedCouponItemSchema = z.object({
  coupon_id: CouponIdSchema,
  title: z.string().min(1),
  coupon_description: z.string().min(1),
  start_at: Iso8601DateTimeSchema,
  end_at: Iso8601DateTimeSchema.optional(),
  get_type: CouponGetTypeSchema,
  use_type: CouponUseTypeSchema,
  use_list: z.array(z.string()).optional(),
  use_targets: z.array(CouponUseTargetSchema).optional(),
  discount_type: CouponDiscountTypeSchema,
  value: z.number().int(),
  usable_period: z.number().int().optional(),
  usable_start_at: Iso8601DateTimeSchema.optional(),
  usable_end_at: Iso8601DateTimeSchema.optional(),
  usable_count: CouponUsableCountSchema,
  is_combine_usable: z.boolean(),
  buy_now_only: z.boolean(),
  buy_cart_only: z.boolean(),
  status: OwnedCouponStatusSchema,
  remaining_count: z.number().int().min(-1),
  used_count: z.number().int().min(0),
  obtained_at: Iso8601DateTimeSchema,
  usable_from: Iso8601DateTimeSchema,
  expires_at: Iso8601DateTimeSchema.optional()
});

export const UserCouponsResponseSchema = z.object({
  claimable: z.array(ClaimableCouponItemSchema),
  owned: z.array(OwnedCouponItemSchema)
});

export const CouponResponseSchema = z.object({
  coupon_id: CouponIdSchema,
  title: z.string().min(1),
  coupon_description: z.string().min(1),
  start_at: Iso8601DateTimeSchema,
  end_at: Iso8601DateTimeSchema.optional(),
  get_type: CouponGetTypeSchema,
  use_type: CouponUseTypeSchema,
  use_list: z.array(z.string()).optional(),
  use_targets: z.array(CouponUseTargetSchema).optional(),
  discount_type: CouponDiscountTypeSchema,
  value: z.number().int(),
  usable_period: z.number().int().optional(),
  usable_start_at: Iso8601DateTimeSchema.optional(),
  usable_end_at: Iso8601DateTimeSchema.optional(),
  usable_count: CouponUsableCountSchema,
  is_combine_usable: z.boolean(),
  buy_now_only: z.boolean(),
  buy_cart_only: z.boolean(),
  claimed: z.boolean()
});

export const ProductCouponsResponseSchema = z.object({
  coupons: z.array(ClaimableCouponItemSchema)
});

export const CouponClaimResponseSchema = z.object({
  coupon_id: CouponIdSchema,
  obtained_at: Iso8601DateTimeSchema,
  remaining_count: z.number().int().min(-1),
  usable_from: Iso8601DateTimeSchema,
  expires_at: Iso8601DateTimeSchema.optional()
});

export const CouponClaimConflictErrorCodeSchema = z.enum(['COUPON_ALREADY_CLAIMED', 'COUPON_OUTSIDE_DISTRIBUTION_PERIOD']);

export const CouponClaimConflictResponseSchema = z.object({
  errorCode: CouponClaimConflictErrorCodeSchema,
  detail: z.string(),
  status: z.number().int()
});

export const EngagementTypeSchema = z.enum(['favorite', 'sequel']);

export const EngagementCreateRequestSchema = z.object({
  product_group_name: z.string().min(1)
});

export const EngagementItemSchema = z.object({
  product_group_name: z.string(),
  product_group_title: z.string(),
  created_at: Iso8601DateTimeSchema,
  author: z.array(z.string().min(1)).optional(),
  cover_image: CoverImageSchema.optional(),
  latest_product_name: JdcnSchema.optional(),
  magazine_name: AlphanumericSlugSchema.optional(),
  magazine_title: z.string().optional(),
  magazine_series_name: AlphanumericSlugSchema.optional(),
  magazine_series_title: z.string().optional(),
  category: z.array(CategoryMetaSchema).optional(),
  introduction: IntroductionSchema.optional()
});

export const EngagementListResponseSchema = z.object({
  filter_list: z.array(z.string()).optional(),
  items: z.array(EngagementItemSchema)
});

export const EngagementMutationResponseSchema = z.object({
  product_group_name: z.string(),
  is_registered: z.boolean(),
  already_registered: z.boolean()
});

export const EngagementBulkDeleteRequestSchema = z.object({
  product_group_names: z.array(z.string().min(1)).min(1).max(500)
});

export const EngagementStatusSchema = z.object({
  product_group_name: z.string(),
  is_favorite: z.boolean(),
  is_sequel_notification_enabled: z.boolean()
});

export const EngagementStatusResponseSchema = z.object({
  statuses: z.array(EngagementStatusSchema)
});

export const EngagementErrorCodeSchema = z.enum(['VALIDATION_FAILED', 'VALIDATION_FIELD_MISSING', 'VALIDATION_FIELD_INVALID', 'PLATFORM_PARAM_INVALID', 'AUTH_TOKEN_MISSING', 'AUTH_TOKEN_INVALID', 'AUTH_TOKEN_EXPIRED', 'USER_PROFILE_NOT_FOUND', 'ENGAGEMENT_GROUP_NOT_FOUND', 'ENGAGEMENT_LIMIT_EXCEEDED', 'CONFIG_MISSING', 'INTERNAL_UNEXPECTED_ERROR']);

export const EngagementErrorResponseSchema = z.object({
  errorCode: EngagementErrorCodeSchema,
  detail: z.string(),
  status: z.number().int(),
  isRetryable: z.boolean()
});

export const IncentiveTypeSchema = z.enum(['regist', 'purchase', 'login']);

export const IncentiveValueTypeSchema = z.enum(['point', 'percent', 'page', 'coupon']);

export const IncentivePurchaseTypeSchema = z.enum(['product', 'group']);

export const IncentivePlatformSchema = z.object({
  web: z.boolean(),
  ios: z.boolean(),
  android: z.boolean()
});

export const IncentiveCsvUploadSchema = z.object({
  incentive_id: z.string().regex(new RegExp("^[A-Za-z0-9_-]{16,64}$")),
  title: z.string().min(1),
  start_at: CsvDateTimeSchema,
  end_at: CsvDateTimeSchema.optional(),
  incentive_type: IncentiveTypeSchema,
  purchase_type: IncentivePurchaseTypeSchema.optional(),
  purchase_conditions: z.string().optional(),
  incentive_value_type: IncentiveValueTypeSchema,
  incentive_value: z.string().min(1),
  exclude_coupon_used: z.string().regex(new RegExp("^[01]$")).optional(),
  platform: z.string().regex(new RegExp("^[01]{3}$"))
});

export const IncentiveMasterDbSchema = z.object({
  pk: z.string(),
  sk: z.string(),
  incentive_id: z.string().regex(new RegExp("^[A-Za-z0-9_-]{16,64}$")),
  title: z.string().min(1),
  start_at: Iso8601DateTimeSchema,
  end_at: Iso8601DateTimeSchema.optional(),
  incentive_type: IncentiveTypeSchema,
  purchase_type: IncentivePurchaseTypeSchema.optional(),
  purchase_conditions: z.array(z.string()).optional(),
  incentive_value_type: IncentiveValueTypeSchema,
  incentive_value: z.string().min(1),
  exclude_coupon_used: z.boolean().optional(),
  platform: IncentivePlatformSchema,
  gsi2pk: IncentiveTypeSchema,
  created_at: Iso8601DateTimeSchema,
  updated_at: Iso8601DateTimeSchema
});

export const UserIncentiveItemSchema = z.object({
  incentiveId: z.string().regex(new RegExp("^[A-Za-z0-9_-]{16,64}$")),
  title: z.string().min(1),
  incentiveType: IncentiveTypeSchema,
  incentiveValueType: IncentiveValueTypeSchema,
  grantedAt: Iso8601DateTimeSchema,
  couponId: CouponIdSchema.optional()
});

export const UserIncentivesResponseSchema = z.object({
  incentives: z.array(UserIncentiveItemSchema)
});

export const IncentiveErrorCodeSchema = z.enum(['VALIDATION_FIELD_INVALID', 'VALIDATION_FIELD_MISSING', 'AUTH_TOKEN_INVALID', 'USER_SESSION_EXPIRED', 'USER_PROFILE_NOT_FOUND', 'RESOURCE_NOT_FOUND', 'CONFIG_MISSING', 'INTERNAL_UNEXPECTED_ERROR', 'INTERNAL_DATABASE_ERROR']);

export const IncentiveErrorResponseSchema = z.object({
  errorCode: IncentiveErrorCodeSchema,
  detail: z.string(),
  status: z.number().int(),
  isRetryable: z.boolean()
});

export const IncentiveUnauthorizedResponseSchema = z.object({
  message: z.string().optional(),
  errorCode: IncentiveErrorCodeSchema.optional(),
  detail: z.string().optional(),
  status: z.number().int().optional()
});

export const SpecialTypeSchema = z.enum(['product', 'campaign', 'incentive']);

export const SpecialColorCodeSchema = z.string().regex(new RegExp("^#[0-9a-fA-F]{6}$"));

export const SpecialColorsSchema = z.object({
  background: SpecialColorCodeSchema.optional(),
  text: SpecialColorCodeSchema.optional(),
  block_header_background: SpecialColorCodeSchema.optional(),
  block_header_txt: SpecialColorCodeSchema.optional(),
  btn_txt: SpecialColorCodeSchema.optional(),
  tbtn: SpecialColorCodeSchema.optional(),
  rbtn: SpecialColorCodeSchema.optional(),
  fbtn: SpecialColorCodeSchema.optional(),
  cbtn: SpecialColorCodeSchema.optional()
});

export const SpecialBlockTypeSchema = z.enum(['static', 'static_combine', 'slide', 'static_with_slide']);

export const SpecialBlockBaseSchema = z.object({
  block_id: AlphanumericSlugSchema,
  block_type: SpecialBlockTypeSchema,
  title: z.string().optional(),
  block_start_at: Iso8601DateTimeSchema.optional(),
  block_stop_at: Iso8601DateTimeSchema.optional(),
  colors: SpecialColorsSchema.optional()
});

export const SpecialItemTypeSchema = z.enum(['image', 'banner', 'coupon', 'youtube', 'txt', 'btn', 'product', 'product_detail']);

export const SpecialItemBaseSchema = z.object({
  item_type: SpecialItemTypeSchema,
  item: z.string().min(1),
  link_to: z.string().optional(),
  link_to_target: z.string().optional(),
  title: z.string().optional(),
  colors: SpecialColorsSchema.optional()
});

export const SpecialProductItemSchema = z.object({
  product_name: JdcnSchema,
  cover_image: CoverImageSchema,
  volume_title: z.string().min(1),
  introduction: z.string().optional(),
  title_title: z.string().optional(),
  volume_number_title: z.string().optional(),
  price: PriceSchema,
  discount_price: PriceSchema.optional(),
  product_icon: z.string().optional(),
  product_catch: z.string().optional(),
  is_free_streaming: z.boolean().optional(),
  author: z.array(z.string().min(1)).optional(),
  magazine_name: AlphanumericSlugSchema.optional(),
  magazine_title: z.string().optional(),
  category: z.array(CategoryMetaSchema).optional(),
  release_at: Iso8601DateTimeSchema.optional()
});

export const SpecialResponseItemSchema = z.object({
  item_type: SpecialItemTypeSchema,
  item: z.string().min(1),
  link_to: z.string().optional(),
  link_to_target: z.string().optional(),
  title: z.string().optional(),
  colors: SpecialColorsSchema.optional(),
  product: SpecialProductItemSchema.optional()
});

export const SpecialResponseBlockSchema = z.object({
  block_id: AlphanumericSlugSchema,
  block_type: SpecialBlockTypeSchema,
  title: z.string().optional(),
  block_start_at: Iso8601DateTimeSchema.optional(),
  block_stop_at: Iso8601DateTimeSchema.optional(),
  colors: SpecialColorsSchema.optional(),
  items: z.array(SpecialResponseItemSchema).optional()
});

export const IncentivePageResponseSchema = z.object({
  special_type: SpecialTypeSchema,
  title: z.string().min(1),
  colors: SpecialColorsSchema.optional(),
  blocks: z.array(SpecialResponseBlockSchema)
});

export const InformationNameSchema = z.string().regex(new RegExp("^[A-Za-z0-9_]{1,64}$"));

export const InformationPlatformSchema = z.object({
  web: z.boolean(),
  ios: z.boolean(),
  android: z.boolean()
});

export const InformationStatusSchema = z.enum(['scheduled', 'published', 'expired']);

export const InformationBodySchema = z.string().min(1).max(2000);

export const InformationCsvUploadSchema = z.object({
  information_name: InformationNameSchema,
  title: z.string().min(1).max(100),
  start_at: CsvDateTimeSchema,
  end_at: CsvDateTimeSchema.optional(),
  platform: z.string().regex(new RegExp("^[01]{3}$")),
  body: InformationBodySchema
});

export const InformationDbSchema = z.object({
  information_name: InformationNameSchema,
  data_type: z.enum(['INFORMATION']),
  title: z.string().min(1).max(100),
  body: InformationBodySchema,
  start_at: Iso8601DateTimeSchema,
  end_at: Iso8601DateTimeSchema.optional(),
  platform: InformationPlatformSchema,
  created_at: Iso8601DateTimeSchema,
  updated_at: Iso8601DateTimeSchema
});

export const InformationListItemSchema = z.object({
  information_name: InformationNameSchema,
  title: z.string(),
  start_at: Iso8601DateTimeSchema,
  end_at: Iso8601DateTimeSchema.optional()
});

export const InformationListResponseSchema = z.object({
  informations: z.array(InformationListItemSchema),
  next_cursor: z.string().optional()
});

export const InformationResponseSchema = z.object({
  information_name: InformationNameSchema,
  title: z.string(),
  body: InformationBodySchema,
  start_at: Iso8601DateTimeSchema,
  end_at: Iso8601DateTimeSchema.optional()
});

export const AdminInformationItemSchema = z.object({
  information_name: InformationNameSchema,
  title: z.string(),
  body: InformationBodySchema,
  start_at: Iso8601DateTimeSchema,
  end_at: Iso8601DateTimeSchema.optional(),
  platform: InformationPlatformSchema,
  status: InformationStatusSchema,
  created_at: Iso8601DateTimeSchema,
  updated_at: Iso8601DateTimeSchema
});

export const AdminInformationListResponseSchema = z.object({
  informations: z.array(AdminInformationItemSchema),
  next_cursor: z.string().optional()
});

export const LotoRateNameSchema = z.string().regex(new RegExp("^[a-zA-Z0-9_\\-]+$")).max(64);

export const LotoStatusReasonSchema = z.enum(['ALREADY_DRAWN', 'NOT_IN_PERIOD']);

export const LotoRateSchema = z.object({
  rate_name: LotoRateNameSchema,
  title: z.string().max(128).optional(),
  chance_rate: z.number().int().min(0).max(1000000),
  win_point: z.number().int().min(0),
  rate_image: z.string().max(256).optional(),
  rate_description: z.string().max(512)
});

export const LotoConfigBaseSchema = z.object({
  loto_name: z.string().regex(new RegExp("^[a-zA-Z0-9_\\-]+$")).max(64),
  start_at: z.string().regex(new RegExp("^\\d{4}-\\d{2}-\\d{2}$")),
  end_at: z.string().regex(new RegExp("^\\d{4}-\\d{2}-\\d{2}$")),
  frequency: z.number().int().min(1),
  times: z.number().int().min(1),
  loto_image: z.string().max(256).optional()
});

export const LotoConfigCsvUploadSchema = z.object({
  loto_name: z.string().regex(new RegExp("^[a-zA-Z0-9_\\-]+$")).max(64),
  start_at: z.string().regex(new RegExp("^\\d{4}-\\d{2}-\\d{2}$")),
  end_at: z.string().regex(new RegExp("^\\d{4}-\\d{2}-\\d{2}$")),
  frequency: z.number().int().min(1),
  times: z.number().int().min(1),
  loto_image: z.string().max(256).optional(),
  rates: z.array(LotoRateSchema).min(1)
});

export const LotoConfigDbSchema = z.object({
  loto_name: z.string().regex(new RegExp("^[a-zA-Z0-9_\\-]+$")).max(64),
  start_at: z.string().regex(new RegExp("^\\d{4}-\\d{2}-\\d{2}$")),
  end_at: z.string().regex(new RegExp("^\\d{4}-\\d{2}-\\d{2}$")),
  frequency: z.number().int().min(1),
  times: z.number().int().min(1),
  loto_image: z.string().max(256).optional(),
  pk: z.string(),
  sk: z.string(),
  rates: z.array(LotoRateSchema).min(1),
  uploaded_at: Iso8601DateTimeSchema,
  uploaded_by: z.string().optional(),
  source_file: z.string().optional(),
  created_at: Iso8601DateTimeSchema,
  updated_at: Iso8601DateTimeSchema
});

export const LotoResultDbSchema = z.object({
  pk: z.string(),
  sk: z.string(),
  loto_result_id: z.string(),
  period_key: z.string(),
  draw_date: z.string().regex(new RegExp("^\\d{4}-\\d{2}-\\d{2}$")),
  seq: z.number().int().min(1),
  config_source_file: z.string().optional(),
  rate_name: z.string().min(1),
  win_point: z.number().int().min(0),
  win_coin: z.number().int().min(0),
  rate_description: z.string().min(1),
  rate_image: z.string().optional(),
  platform: RequestPlatformSchema,
  created_at: Iso8601DateTimeSchema,
  gsi1pk: z.string(),
  gsi1sk: z.string()
});

export const LotoDrawRequestSchema = z.object({
  platform: RequestPlatformSchema
});

export const LotoStatusResponseSchema = z.object({
  is_drawable: z.boolean(),
  times: z.number().int().min(1).optional(),
  remaining_count: z.number().int().min(0).optional(),
  next_drawable_at: Iso8601DateTimeSchema.optional(),
  loto_image: z.string().optional(),
  reason: LotoStatusReasonSchema.optional()
});

export const LotoDrawResponseSchema = z.object({
  loto_result_id: z.string(),
  rate_name: z.string().min(1),
  win_point: z.number().int().min(0),
  win_coin: z.number().int().min(0),
  rate_description: z.string().min(1),
  rate_image: z.string().optional(),
  remaining_count: z.number().int().min(0),
  next_drawable_at: Iso8601DateTimeSchema
});

export const LotoErrorCodeSchema = z.enum(['VALIDATION_FAILED', 'VALIDATION_FIELD_MISSING', 'VALIDATION_FIELD_INVALID', 'AUTH_TOKEN_INVALID', 'USER_PROFILE_NOT_FOUND', 'LT001', 'LT005', 'CONFIG_MISSING', 'INTERNAL_DATABASE_ERROR', 'INTERNAL_UNEXPECTED_ERROR']);

export const LotoErrorResponseSchema = z.object({
  errorCode: LotoErrorCodeSchema,
  detail: z.string(),
  status: z.number().int(),
  isRetryable: z.boolean()
});

export const MenuItemViewTypeSchema = z.enum(['hidden', 'single', 'double']);

export const MenuBlockViewTypeSchema = z.enum(['hidden', 'single']);

export const MenuPlatformSchema = z.object({
  web: z.boolean(),
  ios: z.boolean(),
  android: z.boolean()
});

export const MenuItemBaseSchema = z.object({
  title: z.string().max(25).optional(),
  link_to: z.string().min(1),
  link_to_target: z.string().optional(),
  view_type: MenuItemViewTypeSchema
});

export const MenuItemSchema = z.object({
  title: z.string().max(25).optional(),
  link_to: z.string().min(1),
  link_to_target: z.string().optional(),
  view_type: MenuItemViewTypeSchema,
  platform: MenuPlatformSchema
});

export const MenuBlockBaseSchema = z.object({
  title: z.string().max(25).optional(),
  view_type: MenuBlockViewTypeSchema
});

export const MenuResponseBlockSchema = z.object({
  title: z.string().max(25).optional(),
  view_type: MenuBlockViewTypeSchema,
  items: z.array(MenuItemBaseSchema).min(1)
});

export const MenuBlockSchema = z.object({
  title: z.string().max(25).optional(),
  view_type: MenuBlockViewTypeSchema,
  platform: MenuPlatformSchema,
  items: z.array(MenuItemSchema).min(1)
});

export const MenuSetSchema = z.object({
  menu_id: AlphanumericSlugSchema,
  start_at: Iso8601DateTimeSchema,
  stop_at: Iso8601DateTimeSchema.optional(),
  blocks: z.array(MenuBlockSchema).min(1),
  updated_at: Iso8601DateTimeSchema.optional()
});

export const MenuResponseSchema = z.object({
  menu_id: AlphanumericSlugSchema.optional(),
  blocks: z.array(MenuResponseBlockSchema)
});

export const PaymentOrderIdSchema = z.string().regex(new RegExp("^[a-zA-Z0-9]{32}$"));

export const PaymentPurposeSchema = z.enum(['COIN_PURCHASE', 'BOOK_PURCHASE']);

export const PaymentOrderProviderIdSchema = z.enum(['coin', 'gmo_credit', 'gmo_paypay', 'rakutenpay', 'apple_iap', 'google_iap', 'none']);

export const PaymentCurrencySchema = z.string().regex(new RegExp("^[A-Z]{3}$"));

export const PaymentOrderStatusSchema = z.enum(['DRAFT', 'CREATED', 'PENDING', 'AUTHORIZED', 'CAPTURED', 'FULFILLED', 'FAILED', 'EXPIRED', 'SUPERSEDED', 'REFUNDED', 'USER_CANCELED', 'MISMATCH', 'REFUND_REQUIRED']);

export const PaymentFlowTypeSchema = z.enum(['redirect', 'qr_wait', 'none', 'store_native']);

export const PaymentButtonKindSchema = z.enum(['generic', 'provider_script']);

export const PaymentChargeTypeSchema = z.enum(['ONETIME', 'ONFILE']);

export const CardBrandSchema = z.enum(['VISA', 'MASTER', 'JCB', 'AMEX', 'DINERS', 'OTHER']);

export const PaymentProductLineSchema = z.object({
  jdcn: JdcnSchema,
  jdcn_list: z.array(JdcnSchema).min(1),
  product_title: z.string(),
  title_title: z.string().max(80).optional(),
  volume_number_title: z.string().max(100).optional(),
  cover_image_key: z.string().optional(),
  price_ex: z.number().int().min(0),
  discount_ex: z.number().int().min(0),
  pay_price_ex: z.number().int().min(0),
  excluded_jdcns: z.array(JdcnSchema).optional()
});

export const PaymentOrderSummarySchema = z.object({
  jdcn: JdcnSchema,
  product_title: z.string()
});

export const PaymentDiscountTypeSchema = z.enum(['COUPON', 'CAMPAIGN']);

export const PaymentDiscountSchema = z.object({
  type: PaymentDiscountTypeSchema,
  code: z.string().optional(),
  amount: z.number().int().min(0)
});

export const PointPlanNameSchema = z.string().regex(new RegExp("^[A-Za-z0-9_-]{3,64}$"));

export const PointItemNameSchema = z.string().regex(new RegExp("^[A-Za-z0-9._-]{1,100}$"));

export const PaymentCoinPlanSchema = z.object({
  plan_name: PointPlanNameSchema,
  item_name: PointItemNameSchema,
  display_name: z.string().min(1).max(50),
  coin_paid: z.number().int().min(0),
  coin_bonus: z.number().int().min(0)
});

export const PaymentCoinBackBasisSchema = z.enum(['AFTER_DISCOUNT']);

export const PaymentCoinBackSchema = z.object({
  amount: z.number().int().min(0),
  basis: PaymentCoinBackBasisSchema
});

export const PaymentCoinUsageLineSchema = z.object({
  balance: z.number().int().min(0),
  use: z.number().int().min(0),
  balance_after: z.number().int().min(0)
});

export const PaymentCoinUsageSchema = z.object({
  amount: z.number().int().min(0),
  bonus: PaymentCoinUsageLineSchema,
  paid: PaymentCoinUsageLineSchema
});

export const PaymentCardSummarySchema = z.object({
  brand: CardBrandSchema,
  last4: z.string().regex(new RegExp("^[0-9]{4}$"))
});

export const PaymentStoredCardSchema = z.object({
  card_ref: z.string(),
  brand: CardBrandSchema,
  last4: z.string().regex(new RegExp("^[0-9]{4}$")),
  exp: z.string().regex(new RegExp("^[0-9]{2}/[0-9]{2}$")).optional(),
  is_expired: z.boolean().optional()
});

export const PaymentButtonConfigSchema = z.object({
  src: z.string().optional(),
  class_name: z.string().optional(),
  width: z.number().int().optional()
});

export const PaymentRenderDirectiveSchema = z.object({
  provider_id: PaymentOrderProviderIdSchema,
  provider_label: z.string(),
  icon_label: z.string().regex(new RegExp("^https://[^\\s]+$")).min(1).max(500).optional(),
  flow_type: PaymentFlowTypeSchema,
  requires_card_input: z.boolean(),
  cards: z.array(PaymentStoredCardSchema).optional(),
  button_kind: PaymentButtonKindSchema,
  button_config: PaymentButtonConfigSchema.optional(),
  confirm_label: z.string()
});

export const PaymentNextActionTypeSchema = z.enum(['redirect', 'qr_wait', 'none']);

export const PaymentNextActionMethodSchema = z.enum(['GET', 'POST']);

export const PaymentNextActionSchema = z.object({
  type: PaymentNextActionTypeSchema,
  url: z.string().optional(),
  method: PaymentNextActionMethodSchema.optional(),
  poll_after_ms: z.number().int().min(0).optional()
});

export const PaymentFailureSchema = z.object({
  reason: z.string(),
  message: z.string().optional()
});

export const PaymentProviderIdSchema = z.enum(['coin', 'gmo_credit', 'gmo_paypay', 'rakutenpay', 'apple_iap', 'google_iap']);

export const PaymentCheckoutRequestSchema = z.object({
  purpose: PaymentPurposeSchema,
  platform: RequestPlatformSchema,
  item_name: PointItemNameSchema.optional(),
  jdcn_list: z.array(JdcnSchema).min(1).max(300).optional(),
  coupon_id: CouponIdSchema.optional(),
  provider_id: PaymentProviderIdSchema,
  quote_hash: z.string().regex(new RegExp("^[0-9a-f]{64}$")),
  return_path: z.string().optional()
});

export const PaymentCreditInputSchema = z.object({
  token: z.string().optional(),
  store_card: z.boolean().optional(),
  card_ref: z.string().optional()
});

export const PaymentIntentRequestSchema = z.object({
  order_id: PaymentOrderIdSchema,
  credit: PaymentCreditInputSchema.optional()
});

export const PaymentCheckoutResponseSchema = z.object({
  order_id: PaymentOrderIdSchema,
  purpose: PaymentPurposeSchema,
  subtotal: z.number().int().min(0),
  discount_total: z.number().int().min(0),
  amount: z.number().int().min(0),
  currency: PaymentCurrencySchema,
  tax: z.number().int().min(0).optional(),
  product_count: z.number().int().min(0).optional(),
  product_list: z.array(PaymentProductLineSchema).max(300).optional(),
  coin_plan: PaymentCoinPlanSchema.optional(),
  discounts: z.array(PaymentDiscountSchema).optional(),
  coin_back: PaymentCoinBackSchema.optional(),
  coin_usage: PaymentCoinUsageSchema.optional(),
  expires_in: z.number().int().min(0),
  payment: PaymentRenderDirectiveSchema
});

export const PaymentGrantResultSchema = z.object({
  product_count: z.number().int().min(0).optional(),
  coin_amount: z.number().int().min(0).optional()
});

export const PaymentIntentResponseSchema = z.object({
  order_id: PaymentOrderIdSchema,
  status: PaymentOrderStatusSchema,
  amount: z.number().int().min(0).optional(),
  granted: PaymentGrantResultSchema.optional(),
  next_action: PaymentNextActionSchema
});

export const PaymentOrderResponseSchema = z.object({
  order_id: PaymentOrderIdSchema,
  status: PaymentOrderStatusSchema,
  purpose: PaymentPurposeSchema,
  provider_id: PaymentOrderProviderIdSchema.optional(),
  provider_label: z.string().optional(),
  charge_type: PaymentChargeTypeSchema.optional(),
  subtotal: z.number().int().min(0).optional(),
  discount_total: z.number().int().min(0).optional(),
  amount: z.number().int().min(0),
  currency: PaymentCurrencySchema.optional(),
  tax: z.number().int().min(0).optional(),
  product_count: z.number().int().min(0).optional(),
  product_list: z.array(PaymentProductLineSchema).max(300).optional(),
  discounts: z.array(PaymentDiscountSchema).optional(),
  coin_back: PaymentCoinBackSchema.optional(),
  coin_plan: PaymentCoinPlanSchema.optional(),
  coin_usage: PaymentCoinUsageSchema.optional(),
  expires_in: z.number().int().min(0).optional(),
  card: PaymentCardSummarySchema.optional(),
  payment: PaymentRenderDirectiveSchema.optional(),
  next_action: PaymentNextActionSchema.optional(),
  failure: PaymentFailureSchema.optional(),
  created_at: Iso8601DateTimeSchema,
  settled_at: Iso8601DateTimeSchema.optional()
});

export const PaymentOrderListProductSchema = z.object({
  jdcn: JdcnSchema,
  product_title: z.string().min(1),
  amount_incl_tax: z.number().int().min(0)
});

export const PaymentReceiptSchema = z.object({
  issued_on: z.string(),
  amount: z.number().int().min(0),
  tax: z.number().int().min(0)
});

export const PaymentOrderListItemSchema = z.object({
  order_id: PaymentOrderIdSchema,
  amount: z.number().int().min(0),
  tax: z.number().int().min(0),
  products: z.array(PaymentOrderListProductSchema).min(1).max(300),
  receipt: PaymentReceiptSchema,
  settled_at: Iso8601DateTimeSchema
});

export const PaymentOrderListResponseSchema = z.object({
  year: z.number().int().min(2013),
  has_history: z.boolean(),
  orders: z.array(PaymentOrderListItemSchema)
});

export const PaymentErrorCodeSchema = z.enum(['VALIDATION_FAILED', 'VALIDATION_FIELD_MISSING', 'VALIDATION_FIELD_INVALID', 'TOKEN_EXPIRED', 'INVALID_CARD_REF', 'PLATFORM_NOT_ALLOWED', 'AMOUNT_OUT_OF_RANGE', 'AUTH_TOKEN_INVALID', 'USER_SESSION_EXPIRED', 'PAYMENT_DECLINED', 'USER_PROFILE_NOT_FOUND', 'RESOURCE_NOT_FOUND', 'ALREADY_OWNED', 'QUOTE_STALE', 'PRICE_CHANGED', 'PAYMENT_IN_PROGRESS', 'INSUFFICIENT_COIN_BALANCE', 'COIN_BALANCE_LIMIT_EXCEEDED', 'CARD_LIMIT_EXCEEDED', 'COUPON_NOT_APPLICABLE', 'COUPON_EXPIRED', 'COUPON_NO_REMAINING', 'IDEMPOTENCY_CONFLICT', 'DRAFT_EXPIRED', 'RATE_LIMITED', 'CONFIG_MISSING', 'INTERNAL_UNEXPECTED_ERROR', 'INTERNAL_DATABASE_ERROR', 'PAYMENT_PROVIDER_ERROR', 'PROVIDER_SUSPENDED', 'USER_SESSION_UNVERIFIABLE']);

export const PaymentErrorContextSchema = z.object({
  conflicting_jdcns: z.array(JdcnSchema).optional(),
  shortage_coin: z.number().int().min(0).optional(),
  current_coin_balance: z.number().int().min(0).optional(),
  min_amount: z.number().int().min(0).optional(),
  max_amount: z.number().int().min(0).optional()
});

export const PaymentErrorResponseSchema = z.object({
  errorCode: PaymentErrorCodeSchema,
  detail: z.string(),
  status: z.number().int(),
  isRetryable: z.boolean(),
  context: PaymentErrorContextSchema.optional()
});

export const PaymentUnauthorizedResponseSchema = z.object({
  message: z.string().optional(),
  errorCode: PaymentErrorCodeSchema.optional(),
  detail: z.string().optional(),
  status: z.number().int().optional()
});

export const CheckoutPurposeSchema = z.enum(['COIN_PURCHASE', 'BOOK_PURCHASE']);

export const PaymentProviderStatusSchema = z.enum(['unsupported', 'disabled', 'enabled']);

export const PaymentProviderPlatformDetailSchema = z.object({
  web: PaymentProviderStatusSchema,
  ios: PaymentProviderStatusSchema,
  android: PaymentProviderStatusSchema
});

export const ProviderUnselectableReasonSchema = z.enum(['AMOUNT_OUT_OF_RANGE', 'INSUFFICIENT_COIN_BALANCE']);

export const PaymentProviderDbSchema = z.object({
  PK: z.string(),
  SK: z.string(),
  provider_id: PaymentProviderIdSchema,
  platform: PaymentProviderPlatformDetailSchema,
  purpose: z.array(CheckoutPurposeSchema).min(1),
  label: z.string().min(1).max(50),
  icon_label: z.string().regex(new RegExp("^https://[^\\s]+$")).min(1).max(500).optional(),
  priority: z.number().int().min(0).max(9999),
  min_amount: z.number().int().min(0).max(10000000),
  max_amount: z.number().int().min(1).max(10000000),
  note: z.string().max(200).optional(),
  created_at: Iso8601DateTimeSchema,
  updated_at: Iso8601DateTimeSchema
});

export const RegisteredCardSchema = z.object({
  card_ref: z.string().min(1).max(20),
  brand: z.string().min(1).max(20),
  last4: z.string().regex(new RegExp("^[0-9]{4}$")),
  exp: z.string().regex(new RegExp("^(0[1-9]|1[0-2])/[0-9]{2}$"))
});

export const PaymentProviderPublicSchema = z.object({
  provider_id: PaymentProviderIdSchema,
  provider_label: z.string().min(1).max(50),
  icon_label: z.string().regex(new RegExp("^https://[^\\s]+$")).min(1).max(500).optional(),
  selectable: z.boolean(),
  reason: ProviderUnselectableReasonSchema.optional(),
  reason_message: z.string().max(200).optional(),
  note: z.string().max(200).optional(),
  cards: z.array(RegisteredCardSchema).optional()
});

export const CoinCheckoutItemSchema = z.object({
  item_name: PointItemNameSchema,
  display_name: z.string().min(1).max(50),
  price: z.number().int().min(1).max(1000000),
  base_ecoin: z.number().int().min(0).max(1000000),
  bonus_ecoin: z.number().int().min(0).max(1000000),
  total_ecoin: z.number().int().min(1).max(1000000)
});

export const CoinBalanceSummarySchema = z.object({
  total: z.number().int().min(0),
  paid_total: z.number().int().min(0),
  bonus_total: z.number().int().min(0)
});

export const CoinCheckoutErrorCodeSchema = z.enum(['VALIDATION_FIELD_MISSING', 'VALIDATION_FIELD_INVALID', 'AUTH_TOKEN_INVALID', 'USER_SESSION_EXPIRED', 'USER_PROFILE_NOT_FOUND', 'RESOURCE_NOT_FOUND', 'CONFIG_MISSING', 'INTERNAL_UNEXPECTED_ERROR', 'INTERNAL_DATABASE_ERROR']);

export const CoinCheckoutErrorResponseSchema = z.object({
  errorCode: CoinCheckoutErrorCodeSchema,
  detail: z.string(),
  status: z.number().int(),
  isRetryable: z.boolean()
});

export const CoinCheckoutUnauthorizedResponseSchema = z.object({
  message: z.string().optional(),
  errorCode: CoinCheckoutErrorCodeSchema.optional(),
  detail: z.string().optional(),
  status: z.number().int().optional(),
  isRetryable: z.boolean().optional()
});

export const CheckoutSourceSchema = z.enum(['CART', 'BUY_NOW']);

export const ExclusionReasonSchema = z.enum(['ALREADY_OWNED', 'SALE_STOPPED', 'PRODUCT_UNAVAILABLE']);

export const CouponUnusableReasonSchema = z.enum(['NO_APPLICABLE_LINE', 'NOT_COMBINABLE_WITH_SALE', 'WRONG_PURCHASE_FLOW', 'EXPIRED', 'NOT_YET_USABLE', 'NO_REMAINING']);

export const BookCheckoutItemSchema = z.object({
  jdcn: JdcnSchema,
  jdcn_list: z.array(JdcnSchema).min(1),
  excluded_jdcns: z.array(JdcnSchema).optional(),
  product_title: z.string().min(1).max(200),
  sale_price_ex: z.number().int().min(0).max(10000000),
  line_discount_ex: z.number().int().min(0).max(10000000),
  pay_price_ex: z.number().int().min(0).max(10000000),
  coupon_applied: z.boolean(),
  is_on_sale: z.boolean()
});

export const ExcludedItemSchema = z.object({
  jdcn: JdcnSchema,
  product_title: z.string().max(200).optional(),
  reason: ExclusionReasonSchema
});

export const CheckoutSummarySchema = z.object({
  subtotal: z.number().int().min(0).max(10000000),
  discount_amount: z.number().int().min(0).max(10000000),
  payable_amount: z.number().int().min(0).max(10000000),
  tax: z.number().int().min(0).max(10000000),
  currency: z.enum(['JPY'])
});

export const CheckoutCouponSchema = z.object({
  coupon_id: CouponIdSchema,
  title: z.string().min(1).max(100),
  discount_amount: z.number().int().min(0).max(10000000),
  applicable: z.boolean(),
  reason: CouponUnusableReasonSchema.optional(),
  target_product_count: z.number().int().min(0).optional()
});

export const CheckoutItemTypeSchema = z.enum(['product', 'coin']);

export const CoinBackBasisSchema = z.enum(['AFTER_DISCOUNT']);

export const CoinBackSchema = z.object({
  amount: z.number().int().min(0),
  basis: CoinBackBasisSchema
});

export const BookCheckoutResponseSchema = z.object({
  purpose: z.enum(['BOOK_PURCHASE']),
  platform: RequestPlatformSchema,
  source: CheckoutSourceSchema,
  product_count: z.number().int().min(0).max(300),
  product_list: z.array(BookCheckoutItemSchema).max(300),
  excluded: z.array(ExcludedItemSchema),
  summary: CheckoutSummarySchema,
  coupons: z.array(CheckoutCouponSchema),
  applied_coupon_id: CouponIdSchema.optional(),
  coin_back: CoinBackSchema,
  coin_balance: CoinBalanceSummarySchema,
  payable_by_coin: z.boolean(),
  shortage: z.number().int().min(1).optional(),
  providers: z.array(PaymentProviderPublicSchema),
  quote_hash: z.string().regex(new RegExp("^[0-9a-f]{64}$")),
  quote_expires_at: Iso8601DateTimeSchema
});

export const CoinCheckoutResponseSchema = z.object({
  purpose: z.enum(['COIN_PURCHASE']).optional(),
  platform: RequestPlatformSchema,
  plan_name: PointPlanNameSchema,
  item: CoinCheckoutItemSchema,
  estimated_balance_total: z.number().int().min(0),
  coin_balance: CoinBalanceSummarySchema,
  providers: z.array(PaymentProviderPublicSchema),
  quote_hash: z.string().regex(new RegExp("^[0-9a-f]{64}$")).optional(),
  quote_expires_at: Iso8601DateTimeSchema.optional()
});

export const CheckoutResponseSchema = z.union([BookCheckoutResponseSchema, CoinCheckoutResponseSchema]);

export const CouponApplyRequestSchema = z.object({
  item_type: z.enum(['product']),
  jdcn_list: z.array(JdcnSchema).min(1).max(300)
});

export const CouponApplyResponseSchema = z.object({
  coupon_id: CouponIdSchema,
  applicable: z.boolean(),
  reason: CouponUnusableReasonSchema.optional(),
  discount_amount: z.number().int().min(0).max(10000000),
  target_product_count: z.number().int().min(0),
  product_list: z.array(BookCheckoutItemSchema).max(300),
  summary: CheckoutSummarySchema,
  coin_back: CoinBackSchema,
  payable_by_coin: z.boolean(),
  shortage: z.number().int().min(1).optional(),
  quote_hash: z.string().regex(new RegExp("^[0-9a-f]{64}$")),
  quote_expires_at: Iso8601DateTimeSchema
});

export const CheckoutErrorCodeSchema = z.enum(['VALIDATION_FIELD_MISSING', 'VALIDATION_FIELD_INVALID', 'AUTH_TOKEN_INVALID', 'USER_SESSION_EXPIRED', 'USER_PROFILE_NOT_FOUND', 'RESOURCE_NOT_FOUND', 'CART_EMPTY', 'ALL_ITEMS_OWNED', 'CONFIG_MISSING', 'INTERNAL_UNEXPECTED_ERROR', 'INTERNAL_DATABASE_ERROR']);

export const CheckoutErrorResponseSchema = z.object({
  errorCode: CheckoutErrorCodeSchema,
  detail: z.string(),
  status: z.number().int(),
  isRetryable: z.boolean()
});

export const CheckoutUnauthorizedResponseSchema = z.object({
  message: z.string().optional(),
  errorCode: CheckoutErrorCodeSchema.optional(),
  detail: z.string().optional(),
  status: z.number().int().optional(),
  isRetryable: z.boolean().optional()
});

export const PointPlanEntityTypeSchema = z.enum(['PointPlan', 'PointPlanItem']);

export const PointPlanUploadItemSchema = z.object({
  platform: RequestPlatformSchema,
  item_name: PointItemNameSchema,
  display_name: z.string().min(1).max(50),
  price: z.number().int().min(1).max(1000000),
  bonus_ecoin: z.number().int().min(0).max(1000000),
  total_ecoin: z.number().int().min(1).max(1000000),
  base_ecoin: z.number().int().min(0).max(1000000),
  sort_order: z.number().int().min(1),
  note: z.string().max(200).optional()
});

export const PointPlanUploadSchema = z.object({
  plan_name: PointPlanNameSchema,
  start_at: Iso8601DateTimeSchema,
  end_at: Iso8601DateTimeSchema.optional(),
  note: z.string().max(200).optional(),
  items: z.array(PointPlanUploadItemSchema).min(1).max(300)
});

export const PointPlanDbSchema = z.object({
  PK: z.string(),
  SK: z.string(),
  entity_type: PointPlanEntityTypeSchema,
  plan_name: PointPlanNameSchema,
  start_at: Iso8601DateTimeSchema,
  end_at: Iso8601DateTimeSchema.optional(),
  item_count: z.number().int().min(1),
  note: z.string().max(200).optional(),
  created_at: Iso8601DateTimeSchema,
  updated_at: Iso8601DateTimeSchema,
  GSI1PK: z.string(),
  GSI1SK: z.string()
});

export const PointPlanItemDbSchema = z.object({
  PK: z.string(),
  SK: z.string(),
  entity_type: PointPlanEntityTypeSchema,
  plan_name: PointPlanNameSchema,
  platform: RequestPlatformSchema,
  item_name: PointItemNameSchema,
  display_name: z.string().min(1).max(50),
  price: z.number().int().min(1).max(1000000),
  base_ecoin: z.number().int().min(0).max(1000000),
  bonus_ecoin: z.number().int().min(0).max(1000000),
  total_ecoin: z.number().int().min(1).max(1000000),
  sort_order: z.number().int().min(1),
  note: z.string().max(200).optional()
});

export const PointPlanItemPublicSchema = z.object({
  item_name: PointItemNameSchema,
  display_name: z.string().min(1).max(50),
  price: z.number().int().min(1).max(1000000),
  base_ecoin: z.number().int().min(0).max(1000000),
  bonus_ecoin: z.number().int().min(0).max(1000000),
  total_ecoin: z.number().int().min(1).max(1000000)
});

export const PointPlanItemsResponseSchema = z.object({
  plan_name: PointPlanNameSchema.optional(),
  platform: RequestPlatformSchema,
  items: z.array(PointPlanItemPublicSchema)
});

export const EcoinBalanceResponseSchema = z.object({
  paid_total: z.number().int().min(0),
  bonus_total: z.number().int().min(0),
  total: z.number().int().min(0),
  paid_label: z.string().min(1),
  bonus_label: z.string().min(1)
});

export const EcoinHistoryTxTypeSchema = z.enum(['CONTENT_PURCHASE', 'ECOIN_PURCHASE', 'ADMIN_GRANT', 'ADMIN_DEDUCT', 'COIN_BACK', 'LOTO_BONUS']);

export const EcoinCoinTypeSchema = z.enum(['PAID', 'BONUS']);

export const EcoinHistoryItemSchema = z.object({
  tx_id: z.string().min(1),
  occurred_at: Iso8601DateTimeSchema,
  tx_type: EcoinHistoryTxTypeSchema,
  tx_type_label: z.string().min(1),
  coin_type: EcoinCoinTypeSchema,
  coin_type_label: z.string().min(1),
  amount: z.number().int()
});

export const EcoinHistoryResponseSchema = z.object({
  year: z.number().int(),
  years: z.array(z.number().int()).min(1),
  items: z.array(EcoinHistoryItemSchema)
});

export const ProductBaseSchema = z.object({
  product_id: z.string().optional(),
  product_name: JdcnSchema.optional(),
  product_group_name: z.string().min(1).optional(),
  product_group_title: z.string().min(1).optional(),
  product_group_title_kana: z.string().min(1).optional(),
  product_title: z.string().min(1).optional(),
  product_title_kana: z.string().min(1).optional(),
  product_order: z.number().int().min(0).optional(),
  product_order_title: z.string().optional(),
  file_name: AlphanumericSlugSchema.optional(),
  magazine_series_name: AlphanumericSlugSchema.optional(),
  magazine_series_title: z.string().min(1).optional(),
  magazine_name: AlphanumericSlugSchema.optional(),
  magazine_title: z.string().min(1).optional(),
  book_type: ContentTypeSchema.optional(),
  base_price: PriceSchema.optional(),
  introduction: IntroductionSchema.optional(),
  label: LabelSchema.optional(),
  publish_since: PublishSinceSchema.optional(),
  trial_jdcn: JdcnSchema.optional(),
  platform: PlatformSchema.optional(),
  is_free_streaming: z.boolean().optional(),
  campaign_value: z.string().optional(),
  publisher: PublisherSchema.optional()
});

export const ProductCsvUploadSchema = z.object({
  product_id: z.string().optional(),
  product_name: JdcnSchema,
  product_group_name: z.string().min(1),
  product_group_title: z.string().min(1),
  product_group_title_kana: z.string().min(1).optional(),
  product_title: z.string().min(1),
  product_title_kana: z.string().min(1).optional(),
  product_order: z.number().int().min(0).optional(),
  product_order_title: z.string().optional(),
  file_name: AlphanumericSlugSchema.optional(),
  magazine_series_name: AlphanumericSlugSchema.optional(),
  magazine_series_title: z.string().min(1).optional(),
  magazine_name: AlphanumericSlugSchema.optional(),
  magazine_title: z.string().min(1).optional(),
  book_type: ContentTypeSchema,
  base_price: PriceSchema,
  introduction: IntroductionSchema,
  label: LabelSchema,
  publish_since: PublishSinceSchema.optional(),
  trial_jdcn: JdcnSchema.optional(),
  platform: PlatformSchema,
  is_free_streaming: z.boolean().optional(),
  campaign_value: z.string().optional(),
  publisher: PublisherSchema.optional(),
  is_completed: z.boolean().optional(),
  has_collection: HasCollectionSchema.optional(),
  collection_source_jdcn: JdcnSchema.optional(),
  author_name: z.string().min(1).optional(),
  author_kana: z.string().min(1).optional(),
  author_title: z.string().min(1).optional(),
  is_campaign: z.boolean().optional(),
  campaign_type: CampaignPriceTypeSchema.optional(),
  product_catch: z.string().optional(),
  release_at: CsvDateTimeSchema,
  publish_at: CsvDateTimeSchema,
  start_at: CsvDateTimeSchema,
  stop_at: CsvDateTimeSchema.optional(),
  category: CategoryCsvSchema.optional()
});

export const ProductDbSchema = z.object({
  product_id: z.string().optional(),
  product_name: JdcnSchema,
  product_group_name: z.string().min(1).optional(),
  product_group_title: z.string().min(1).optional(),
  product_group_title_kana: z.string().min(1).optional(),
  product_title: z.string().min(1).optional(),
  product_title_kana: z.string().min(1).optional(),
  product_order: z.number().int().min(0).optional(),
  product_order_title: z.string().optional(),
  file_name: AlphanumericSlugSchema.optional(),
  magazine_series_name: AlphanumericSlugSchema.optional(),
  magazine_series_title: z.string().min(1).optional(),
  magazine_name: AlphanumericSlugSchema.optional(),
  magazine_title: z.string().min(1).optional(),
  book_type: ContentTypeSchema.optional(),
  base_price: PriceSchema.optional(),
  introduction: IntroductionSchema.optional(),
  label: LabelSchema.optional(),
  publish_since: PublishSinceSchema.optional(),
  trial_jdcn: JdcnSchema.optional(),
  platform: PlatformDetailSchema.optional(),
  is_free_streaming: z.boolean().optional(),
  campaign_value: z.string().optional(),
  publisher: PublisherSchema.optional(),
  id: z.string(),
  start_at: Iso8601DateTimeSchema,
  author: z.array(AuthorSchema).optional(),
  sale_price: PriceSchema.optional(),
  has_collection: HasCollectionBooleanSchema.optional(),
  collection_source_jdcn: JdcnSchema.optional(),
  campaign_price_type: CampaignPriceTypeSchema.optional(),
  product_catch: z.string().optional(),
  product_icon: z.string().optional(),
  completed_at: Iso8601DateTimeSchema.optional(),
  release_at: Iso8601DateTimeSchema.optional(),
  publish_at: Iso8601DateTimeSchema.optional(),
  stop_at: Iso8601DateTimeSchema.optional(),
  ttl_timestamp: z.number().int().optional(),
  category: z.array(z.string()).optional(),
  is_campaign: z.boolean().optional(),
  free_catalog_key: z.enum(['FREE']).optional(),
  new_catalog_key: z.enum(['NEW']).optional(),
  created_at: Iso8601DateTimeSchema.optional(),
  updated_at: Iso8601DateTimeSchema.optional()
});

export const ProductResponseSchema = z.object({
  product_id: z.string().optional(),
  product_name: JdcnSchema.optional(),
  product_group_name: z.string().min(1).optional(),
  product_group_title: z.string().min(1).optional(),
  product_group_title_kana: z.string().min(1).optional(),
  product_title: z.string().min(1).optional(),
  product_title_kana: z.string().min(1).optional(),
  product_order: z.number().int().min(0).optional(),
  product_order_title: z.string().optional(),
  file_name: AlphanumericSlugSchema.optional(),
  magazine_series_name: z.string().optional(),
  magazine_series_title: z.string().optional(),
  magazine_name: AlphanumericSlugSchema.optional(),
  magazine_title: z.string().min(1).optional(),
  book_type: ContentTypeSchema.optional(),
  base_price: PriceSchema.optional(),
  introduction: IntroductionSchema.optional(),
  label: LabelSchema.optional(),
  publish_since: PublishSinceSchema.optional(),
  trial_jdcn: JdcnSchema.optional(),
  platform: PlatformSchema.optional(),
  is_free_streaming: z.boolean().optional(),
  campaign_value: z.string().optional(),
  publisher: PublisherSchema.optional(),
  cover_image: CoverImageSchema.optional(),
  category: z.array(z.string()).optional(),
  author: z.array(AuthorSchema).optional(),
  sale_price: PriceSchema.optional(),
  has_collection: HasCollectionBooleanSchema.optional(),
  is_campaign: z.boolean().optional(),
  campaign_price_type: CampaignPriceTypeSchema.optional(),
  product_catch: z.string().optional(),
  product_icon: z.string().optional(),
  release_at: Iso8601DateTimeSchema.optional(),
  publish_at: Iso8601DateTimeSchema.optional(),
  start_at: Iso8601DateTimeSchema.optional(),
  stop_at: Iso8601DateTimeSchema.optional()
});

export const ReservationCreateRequestSchema = z.object({
  jdcn: JdcnSchema
});

export const ReservationItemSchema = z.object({
  jdcn: JdcnSchema,
  title_name: AlphanumericSlugSchema.optional(),
  title_title: z.string().min(1),
  volume_title: z.string().min(1),
  volume_number_title: z.string().optional(),
  created_at: Iso8601DateTimeSchema,
  release_at: Iso8601DateTimeSchema,
  taxed_sale_price: PriceSchema,
  taxed_base_price: PriceSchema,
  sale_status: z.enum(['sale', 'view_only']),
  author: z.array(z.string().min(1)).optional(),
  introduction: IntroductionSchema.optional(),
  cover_image: CoverImageSchema.optional(),
  magazine_series_name: AlphanumericSlugSchema.optional(),
  magazine_series_title: z.string().min(1).optional(),
  magazine_name: AlphanumericSlugSchema.optional(),
  magazine_title: z.string().min(1).optional()
});

export const ReservationListResponseSchema = z.object({
  filter_list: z.array(z.string()).optional(),
  items: z.array(ReservationItemSchema)
});

export const ReservationCreateResponseSchema = z.object({
  jdcn: JdcnSchema,
  is_reserved: z.boolean(),
  already_reserved: z.boolean()
});

export const ReservationBulkDeleteRequestSchema = z.object({
  jdcns: z.array(JdcnSchema).min(1).max(500)
});

export const ReservationStatusSchema = z.object({
  jdcn: JdcnSchema,
  is_reserved: z.boolean()
});

export const ReservationStatusResponseSchema = z.object({
  statuses: z.array(ReservationStatusSchema)
});

export const ReservationErrorCodeSchema = z.enum(['VALIDATION_FAILED', 'VALIDATION_FIELD_MISSING', 'VALIDATION_FIELD_INVALID', 'PLATFORM_PARAM_INVALID', 'AUTH_TOKEN_MISSING', 'AUTH_TOKEN_INVALID', 'AUTH_TOKEN_EXPIRED', 'USER_PROFILE_NOT_FOUND', 'RESERVATION_PRODUCT_NOT_FOUND', 'RESERVATION_NOT_RESERVABLE', 'RESERVATION_IN_PROGRESS', 'CONFIG_MISSING', 'INTERNAL_UNEXPECTED_ERROR']);

export const ReservationErrorResponseSchema = z.object({
  errorCode: ReservationErrorCodeSchema,
  detail: z.string(),
  status: z.number().int(),
  isRetryable: z.boolean()
});

export const ReservationRecordStatusSchema = z.enum(['reserved', 'processing']);

export const ReservationCancelDigestStatusSchema = z.enum(['pending', 'sent']);

export const ReservationCancelReasonSchema = z.enum(['sale_stopped', 'date_changed', 'price_changed']);

export const ReservationDbSchema = z.object({
  pk: z.string().regex(new RegExp("^USER#")),
  sk: z.string().regex(new RegExp("^RESERVATION#[a-zA-Z0-9]{20}$")),
  jdcn: JdcnSchema,
  status: ReservationRecordStatusSchema,
  created_at: Iso8601DateTimeSchema,
  reserved_price: PriceSchema,
  release_scheduled_at: Iso8601DateTimeSchema,
  schedule_key: z.string().optional()
});

export const ReservationCancelEntrySchema = z.object({
  cancel_reason: ReservationCancelReasonSchema,
  title: z.string(),
  release_at: Iso8601DateTimeSchema,
  release_at_before: Iso8601DateTimeSchema.optional(),
  release_at_after: Iso8601DateTimeSchema.optional(),
  price_before: PriceSchema.optional(),
  price_after: PriceSchema.optional()
});

export const ReservationCancelDigestDbSchema = z.object({
  pk: z.string(),
  sk: z.string(),
  data_type: z.enum(['reservation_cancel_digest']),
  status: ReservationCancelDigestStatusSchema,
  entries: z.unknown(),
  created_at: Iso8601DateTimeSchema,
  sent_at: Iso8601DateTimeSchema.optional(),
  attempt_count: z.number().int().optional(),
  last_error: z.string().optional(),
  expires_at: z.number().int()
});

export const AlgoliaFacetFiltersSchema = z.array(z.union([z.string(), z.array(z.string())]));

export const AlgoliaSuggestRequestSchema = z.object({
  query: z.string(),
  hitsPerPage: z.number().int().optional(),
  filters: z.string().optional(),
  restrictSearchableAttributes: z.array(z.string()).optional(),
  attributesToRetrieve: z.array(z.string()).optional(),
  attributesToHighlight: z.array(z.string()).optional(),
  optionalFilters: z.array(z.string()).optional()
});

export const AlgoliaAuthorSuggestRequestSchema = z.object({
  facetQuery: z.string(),
  maxFacetHits: z.number().int().optional(),
  params: z.string().optional()
});

export const AlgoliaSearchRequestSchema = z.object({
  query: z.string(),
  page: z.number().int().optional(),
  hitsPerPage: z.number().int().optional(),
  facetFilters: AlgoliaFacetFiltersSchema.optional(),
  optionalFilters: z.array(z.string()).optional(),
  restrictSearchableAttributes: z.array(z.string()).optional(),
  attributesToRetrieve: z.array(z.string()).optional(),
  attributesToHighlight: z.array(z.string()).optional()
});

export const SearchResultHitSchema = z.object({
  objectID: z.string(),
  title_title: z.string(),
  author_names: z.array(z.string()).optional(),
  magazine_name: z.string().optional(),
  magazine_title: z.string().optional(),
  category: z.array(CategoryMetaSchema).optional(),
  cover_image: z.string().optional(),
  introduction: z.string().optional(),
  publisher: z.string().optional()
});

export const HighlightMatchSchema = z.object({
  value: z.string(),
  matchLevel: z.enum(['none', 'partial', 'full']).optional(),
  matchedWords: z.array(z.string()).optional()
});

export const TitleSuggestHitSchema = z.object({
  objectID: z.string(),
  title_title: z.string(),
  _highlightResult: z.unknown().optional()
});

export const AuthorSuggestHitSchema = z.object({
  value: z.string(),
  highlighted: z.string().optional(),
  count: z.number().int().optional()
});

export const CartGroupItemSchema = z.object({
  jdcn: JdcnSchema,
  title_title: z.string().min(1),
  volume_title: z.string().min(1),
  author: z.array(z.string().min(1)),
  cover_image: CoverImageSchema,
  taxed_sale_price: PriceSchema,
  taxed_base_price: PriceSchema,
  product_order: z.number().int().min(0).optional(),
  sale_status: z.enum(['sale', 'view_only']),
  in_cart: z.boolean(),
  purchased: z.boolean().optional()
});

export const CartGroupResponseSchema = z.object({
  items: z.array(CartGroupItemSchema),
  cart_count: z.number().int().min(0)
});

export const CartGroupErrorCodeSchema = z.enum(['PLATFORM_PARAM_INVALID', 'VALIDATION_FIELD_INVALID', 'AUTH_TOKEN_INVALID', 'CART_GROUP_NOT_FOUND', 'CONFIG_MISSING', 'INTERNAL_DATABASE_ERROR', 'INTERNAL_UNEXPECTED_ERROR']);

export const CartGroupErrorResponseSchema = z.object({
  errorCode: CartGroupErrorCodeSchema,
  detail: z.string(),
  status: z.number().int(),
  isRetryable: z.boolean()
});

export const SpecialPlatformSchema = z.object({
  web: z.boolean(),
  ios: z.boolean(),
  android: z.boolean()
});

export const SeriesNameSchema = z.enum(['AneLaLa', '招き猫文庫', 'Love Silky', 'メロディ', 'MOE', 'シルキー', '黒蜜', '花丸(小説)', 'yomuyonetu', '花丸(コミックス)', '別冊マーガレット', 'ココハナ', 'LaLa', '白泉社マンガPark編集部', '楽園', 'ヤングアニマル', 'その他', 'DRUNK!', 'りぼん', '花とゆめ', 'YOU', 'マーガレット', '白泉社出版部', 'Cookie', 'Love Jossie', 'kodomoe']);

export const SpecialPageStatusSchema = z.enum(['active', 'expired']);

export const SpecialCsvDataTypeSchema = z.enum(['PAGE', 'BLOCK', 'ITEM']);

export const SpecialCsvUploadSchema = z.object({
  data_type: SpecialCsvDataTypeSchema,
  special_type: SpecialTypeSchema.optional(),
  special_name: AlphanumericSlugSchema.optional(),
  start_at: CsvDateTimeSchema.optional(),
  stop_at: CsvDateTimeSchema.optional(),
  special_banner: z.string().optional(),
  block_id: AlphanumericSlugSchema.optional(),
  title: z.string().optional(),
  block_type: SpecialBlockTypeSchema.optional(),
  item_type: SpecialItemTypeSchema.optional(),
  item: z.string().optional(),
  item_link_to: z.string().optional(),
  item_link_target: z.string().optional(),
  platform: z.string().regex(new RegExp("^[01]{3}$")).optional(),
  exclude_on_list: z.boolean().optional(),
  series_name: SeriesNameSchema.optional(),
  product_group: z.string().optional(),
  background_color: SpecialColorCodeSchema.optional(),
  text_color: SpecialColorCodeSchema.optional(),
  block_header_background: SpecialColorCodeSchema.optional(),
  block_header_txt_color: SpecialColorCodeSchema.optional(),
  btn_txt_color: SpecialColorCodeSchema.optional(),
  tbtn_background: SpecialColorCodeSchema.optional(),
  rbtn_background: SpecialColorCodeSchema.optional(),
  fbtn_background: SpecialColorCodeSchema.optional(),
  cbtn_background: SpecialColorCodeSchema.optional(),
  note: z.string().optional()
});

export const SpecialItemSchema = z.object({
  item_type: SpecialItemTypeSchema,
  item: z.string().min(1),
  link_to: z.string().optional(),
  link_to_target: z.string().optional(),
  title: z.string().optional(),
  colors: SpecialColorsSchema.optional(),
  platform: SpecialPlatformSchema.optional()
});

export const SpecialBlockSchema = z.object({
  block_id: AlphanumericSlugSchema,
  block_type: SpecialBlockTypeSchema,
  title: z.string().optional(),
  block_start_at: Iso8601DateTimeSchema.optional(),
  block_stop_at: Iso8601DateTimeSchema.optional(),
  colors: SpecialColorsSchema.optional(),
  platform: SpecialPlatformSchema.optional(),
  items: z.array(SpecialItemSchema).optional()
});

export const SpecialSchema = z.object({
  slug: AlphanumericSlugSchema,
  special_type: SpecialTypeSchema,
  start_at: Iso8601DateTimeSchema,
  stop_at: Iso8601DateTimeSchema.optional(),
  title: z.string().min(1),
  special_banner: z.string().optional(),
  series_name: z.string().optional(),
  exclude_on_list: z.boolean().optional(),
  product_group: z.array(AlphanumericSlugSchema).optional(),
  platform: SpecialPlatformSchema,
  colors: SpecialColorsSchema.optional(),
  blocks: z.array(SpecialBlockSchema).min(1),
  created_at: Iso8601DateTimeSchema,
  updated_at: Iso8601DateTimeSchema,
  note: z.string().optional()
});

export const SpecialResponseSchema = z.object({
  slug: AlphanumericSlugSchema,
  special_type: SpecialTypeSchema,
  page_status: SpecialPageStatusSchema,
  title: z.string().min(1),
  stop_at: Iso8601DateTimeSchema.optional(),
  colors: SpecialColorsSchema.optional(),
  blocks: z.array(SpecialResponseBlockSchema)
});

export const SpecialListItemSchema = z.object({
  slug: AlphanumericSlugSchema,
  title: z.string().min(1),
  series_name: z.string().min(1),
  special_banner: z.string().optional()
});

export const CampaignListItemSchema = z.object({
  slug: AlphanumericSlugSchema,
  stop_at: Iso8601DateTimeSchema.optional(),
  special_banner: z.string().optional()
});

export const SpecialListResponseSchema = z.object({
  filter_list: z.array(z.string()).optional(),
  specials: z.array(SpecialListItemSchema)
});

export const CampaignListResponseSchema = z.object({
  campaigns: z.array(CampaignListItemSchema)
});

export const MagazineCatchUpdateRequestSchema = z.object({
  magazine_catch: z.string().min(0).max(50)
});

export const MagazineCatchUpdateResponseSchema = z.object({
  title_name: AlphanumericSlugSchema,
  magazine_catch: z.string(),
  updated_at: Iso8601DateTimeSchema
});

export const KanaGroupSchema = z.enum(['あ', 'か', 'さ', 'た', 'な', 'は', 'ま', 'や', 'ら', 'わ']);

export const AuthorEntrySchema = z.object({
  author_name: z.string().min(1),
  author_kana: z.string().optional()
});

export const AuthorGroupSchema = z.object({
  filter_list: z.array(KanaGroupSchema).min(1),
  current_filter: KanaGroupSchema,
  authors: z.array(AuthorEntrySchema).min(1)
});

export const AuthorListResponseSchema = z.object({
  filter_list: z.array(KanaGroupSchema).min(1),
  current_filter: KanaGroupSchema,
  authors: z.array(AuthorEntrySchema).min(1)
});

export const CategoryItemSchema = z.object({
  name: z.string().min(1),
  title: z.string().min(1)
});

export const CategoryListResponseSchema = z.object({
  categories: z.array(CategoryItemSchema).min(1)
});

export const DecadeSchema = z.string().regex(new RegExp("^\\d{4}$"));

export const BookGroupEntrySchema = z.object({
  title_name: AlphanumericSlugSchema,
  title_title: z.string().min(1),
  cover_image: CoverImageSchema.optional(),
  author: z.array(z.string().min(1)).optional(),
  magazine_name: AlphanumericSlugSchema.optional(),
  magazine_title: z.string().min(1).optional(),
  category: z.array(CategoryMetaSchema).optional(),
  introduction: IntroductionSchema.optional()
});

export const BookGroupSchema = z.object({
  filter_list: z.array(KanaGroupSchema).min(1),
  current_filter: KanaGroupSchema,
  books: z.array(BookGroupEntrySchema).min(1)
});

export const BookGroupListResponseSchema = z.object({
  filter_list: z.array(KanaGroupSchema).min(1),
  current_filter: KanaGroupSchema,
  books: z.array(BookGroupEntrySchema).min(1)
});

export const BookGroupItemSchema = z.object({
  title_name: AlphanumericSlugSchema,
  title_title: z.string().min(1),
  cover_image: CoverImageSchema.optional(),
  author: z.array(z.string().min(1)).optional(),
  magazine_name: AlphanumericSlugSchema.optional(),
  magazine_title: z.string().min(1).optional(),
  category: z.array(CategoryMetaSchema).optional(),
  introduction: IntroductionSchema.optional(),
  platform: PlatformDetailSchema
});

export const BookGroupSavedSchema = z.object({
  filter_list: z.array(KanaGroupSchema).min(1),
  current_filter: KanaGroupSchema,
  books: z.array(BookGroupItemSchema).min(1)
});

export const MagazineListItemSchema = z.object({
  title_name: AlphanumericSlugSchema,
  magazine_name: AlphanumericSlugSchema,
  magazine_title: z.string().min(1),
  magazine_series_title: z.string().min(1).optional(),
  cover_image: CoverImageSchema.optional(),
  magazine_catch: z.string(),
  latest_release_at: Iso8601DateTimeSchema
});

export const MagazineListResponseSchema = z.object({
  magazines: z.array(MagazineListItemSchema)
});

export const ProductDetailSchema = z.object({
  sale_status: z.enum(['sale', 'view_only']).optional(),
  product_name: JdcnSchema.optional(),
  product_title: z.string().min(1).optional(),
  product_group_name: z.string().min(1).optional(),
  product_group_title: z.string().min(1).optional(),
  magazine_series_name: AlphanumericSlugSchema.optional(),
  magazine_series_title: z.string().min(1).optional(),
  magazine_name: AlphanumericSlugSchema.optional(),
  magazine_title: z.string().min(1).optional(),
  trial_jdcn: JdcnSchema.optional(),
  is_free_streaming: z.boolean().optional(),
  campaign_value: z.string().optional(),
  taxed_base_price: z.number().int().min(0).optional(),
  taxed_sale_price: z.number().int().min(0).optional(),
  cover_image: CoverImageSchema.optional(),
  category: z.array(CategoryMetaSchema).optional(),
  author: z.array(z.string()).optional(),
  introduction: IntroductionSchema.optional(),
  has_collection: HasCollectionBooleanSchema.optional(),
  is_campaign: z.boolean().optional(),
  campaign_price_type: CampaignPriceTypeSchema.optional(),
  product_catch: z.string().optional(),
  product_icon: z.string().optional(),
  release_at: Iso8601DateTimeSchema.optional()
});

export const SpecialProductSchema = z.object({
  slug: AlphanumericSlugSchema,
  special_banner: z.string().min(1).optional()
});

export const ProductGroupSchema = z.object({
  product_name: JdcnSchema.optional(),
  cover_image: CoverImageSchema.optional(),
  product_order_title: z.string().optional(),
  product_icon: z.string().optional()
});

export const ProductGroupAuthorSchema = z.object({
  cover_image: CoverImageSchema.optional(),
  product_group_title: z.string().min(1).optional(),
  product_group_name: z.string().min(1).optional()
});

export const CheckedProductSchema = z.object({
  product_name: JdcnSchema.optional(),
  product_group_title: z.string().min(1).optional(),
  cover_image: CoverImageSchema.optional(),
  product_order_title: z.string().optional(),
  product_icon: z.string().optional()
});

export const ProductDetailPageResponseSchema = z.object({
  product: ProductDetailSchema,
  special_banners: z.array(SpecialProductSchema),
  product_group: z.array(ProductGroupSchema),
  author_group: z.array(ProductGroupAuthorSchema),
  checked_product: z.array(CheckedProductSchema).optional()
});

export const ErrorResponseSchema = z.object({
  message: z.string().optional()
});

export const ProductListNameSchema = z.enum(['new', 'free', 'upcoming']);

export const ProductListResponseSchema = z.object({
  filter_list: z.array(z.string()).optional(),
  products: z.array(ProductListItemSchema)
});

export const SearchKeyResponseSchema = z.object({
  key: z.string().min(1),
  app_id: z.string().min(1),
  index_name: z.string().min(1)
});

export type PresignedUrlRequest = z.infer<typeof PresignedUrlRequestSchema>;
export type PresignedUrlResponse = z.infer<typeof PresignedUrlResponseSchema>;
export type Iso8601DateTime = z.infer<typeof Iso8601DateTimeSchema>;
export type UploadRecord = z.infer<typeof UploadRecordSchema>;
export type UserPassword = z.infer<typeof UserPasswordSchema>;
export type SignupRequest = z.infer<typeof SignupRequestSchema>;
export type RequestPlatform = z.infer<typeof RequestPlatformSchema>;
export type SignupVerifyRequest = z.infer<typeof SignupVerifyRequestSchema>;
export type SignupResendRequest = z.infer<typeof SignupResendRequestSchema>;
export type EmailChangeRequest = z.infer<typeof EmailChangeRequestSchema>;
export type EmailChangeVerifyRequest = z.infer<typeof EmailChangeVerifyRequestSchema>;
export type EmailChangeResendRequest = z.infer<typeof EmailChangeResendRequestSchema>;
export type EmailChangeErrorCode = z.infer<typeof EmailChangeErrorCodeSchema>;
export type EmailChangeErrorResponse = z.infer<typeof EmailChangeErrorResponseSchema>;
export type EmailChangeUnauthorizedResponse = z.infer<typeof EmailChangeUnauthorizedResponseSchema>;
export type VerificationCode = z.infer<typeof VerificationCodeSchema>;
export type PasswordChangeRequest = z.infer<typeof PasswordChangeRequestSchema>;
export type PasswordChangeCodeRequest = z.infer<typeof PasswordChangeCodeRequestSchema>;
export type PasswordChangeCodeResendRequest = z.infer<typeof PasswordChangeCodeResendRequestSchema>;
export type PasswordChangeCodeErrorCode = z.infer<typeof PasswordChangeCodeErrorCodeSchema>;
export type PasswordChangeCodeErrorResponse = z.infer<typeof PasswordChangeCodeErrorResponseSchema>;
export type MessageResponse = z.infer<typeof MessageResponseSchema>;
export type UserEmail = z.infer<typeof UserEmailSchema>;
export type UserPasswordSecret = z.infer<typeof UserPasswordSecretSchema>;
export type PasswordResetRequest = z.infer<typeof PasswordResetRequestSchema>;
export type PasswordResetVerifyRequest = z.infer<typeof PasswordResetVerifyRequestSchema>;
export type PasswordResetResendRequest = z.infer<typeof PasswordResetResendRequestSchema>;
export type PasswordResetErrorCode = z.infer<typeof PasswordResetErrorCodeSchema>;
export type PasswordResetErrorResponse = z.infer<typeof PasswordResetErrorResponseSchema>;
export type DeviceId = z.infer<typeof DeviceIdSchema>;
export type ClientPlatform = z.infer<typeof ClientPlatformSchema>;
export type SigninRequest = z.infer<typeof SigninRequestSchema>;
export type User = z.infer<typeof UserSchema>;
export type Session = z.infer<typeof SessionSchema>;
export type SigninResponse = z.infer<typeof SigninResponseSchema>;
export type RefreshRequest = z.infer<typeof RefreshRequestSchema>;
export type RefreshResponse = z.infer<typeof RefreshResponseSchema>;
export type LogoutRequest = z.infer<typeof LogoutRequestSchema>;
export type DeviceItem = z.infer<typeof DeviceItemSchema>;
export type DeviceListResponse = z.infer<typeof DeviceListResponseSchema>;
export type DeviceSessionValidResponse = z.infer<typeof DeviceSessionValidResponseSchema>;
export type SessionErrorCode = z.infer<typeof SessionErrorCodeSchema>;
export type SessionErrorResponse = z.infer<typeof SessionErrorResponseSchema>;
export type PasswordChangeErrorCode = z.infer<typeof PasswordChangeErrorCodeSchema>;
export type PasswordChangeErrorResponse = z.infer<typeof PasswordChangeErrorResponseSchema>;
export type WithdrawErrorCode = z.infer<typeof WithdrawErrorCodeSchema>;
export type WithdrawErrorResponse = z.infer<typeof WithdrawErrorResponseSchema>;
export type WithdrawUnauthorizedResponse = z.infer<typeof WithdrawUnauthorizedResponseSchema>;
export type MagazineStatusResponse = z.infer<typeof MagazineStatusResponseSchema>;
export type MagazineStatusUpdateRequest = z.infer<typeof MagazineStatusUpdateRequestSchema>;
export type MagazineStatusErrorCode = z.infer<typeof MagazineStatusErrorCodeSchema>;
export type MagazineStatusErrorResponse = z.infer<typeof MagazineStatusErrorResponseSchema>;
export type MagazineStatusUnauthorizedResponse = z.infer<typeof MagazineStatusUnauthorizedResponseSchema>;
export type Jdcn = z.infer<typeof JdcnSchema>;
export type CouponId = z.infer<typeof CouponIdSchema>;
export type AlphanumericSlug = z.infer<typeof AlphanumericSlugSchema>;
export type Platform = z.infer<typeof PlatformSchema>;
export type Publisher = z.infer<typeof PublisherSchema>;
export type ContentType = z.infer<typeof ContentTypeSchema>;
export type ProductContentType = z.infer<typeof ProductContentTypeSchema>;
export type Author = z.infer<typeof AuthorSchema>;
export type CsvDateTime = z.infer<typeof CsvDateTimeSchema>;
export type CsvDate = z.infer<typeof CsvDateSchema>;
export type PublishSince = z.infer<typeof PublishSinceSchema>;
export type Price = z.infer<typeof PriceSchema>;
export type Introduction = z.infer<typeof IntroductionSchema>;
export type Label = z.infer<typeof LabelSchema>;
export type CoverImage = z.infer<typeof CoverImageSchema>;
export type CategoryCsv = z.infer<typeof CategoryCsvSchema>;
export type CategoryMeta = z.infer<typeof CategoryMetaSchema>;
export type SearchKeywords = z.infer<typeof SearchKeywordsSchema>;
export type HasCollection = z.infer<typeof HasCollectionSchema>;
export type HasCollectionBoolean = z.infer<typeof HasCollectionBooleanSchema>;
export type ValidityStatus = z.infer<typeof ValidityStatusSchema>;
export type ProductType = z.infer<typeof ProductTypeSchema>;
export type CampaignPriceType = z.infer<typeof CampaignPriceTypeSchema>;
export type SeriesStatus = z.infer<typeof SeriesStatusSchema>;
export type PlatformStatus = z.infer<typeof PlatformStatusSchema>;
export type PlatformDetail = z.infer<typeof PlatformDetailSchema>;
export type BookBase = z.infer<typeof BookBaseSchema>;
export type BookCsvUpload = z.infer<typeof BookCsvUploadSchema>;
export type BookDb = z.infer<typeof BookDbSchema>;
export type TitleDb = z.infer<typeof TitleDbSchema>;
export type MagazineDb = z.infer<typeof MagazineDbSchema>;
export type BookshelfSort = z.infer<typeof BookshelfSortSchema>;
export type ReadStatus = z.infer<typeof ReadStatusSchema>;
export type BookshelfSortRanks = z.infer<typeof BookshelfSortRanksSchema>;
export type BookshelfSortSections = z.infer<typeof BookshelfSortSectionsSchema>;
export type BookshelfRecentVolume = z.infer<typeof BookshelfRecentVolumeSchema>;
export type BookshelfTitleItem = z.infer<typeof BookshelfTitleItemSchema>;
export type BookshelfResponse = z.infer<typeof BookshelfResponseSchema>;
export type BookshelfVolumeTitle = z.infer<typeof BookshelfVolumeTitleSchema>;
export type ProductListItem = z.infer<typeof ProductListItemSchema>;
export type BookshelfVolumeItem = z.infer<typeof BookshelfVolumeItemSchema>;
export type BookshelfVolumesResponse = z.infer<typeof BookshelfVolumesResponseSchema>;
export type BookshelfSearchResponse = z.infer<typeof BookshelfSearchResponseSchema>;
export type ReadingStateRequest = z.infer<typeof ReadingStateRequestSchema>;
export type ReadingStateUpdateResponse = z.infer<typeof ReadingStateUpdateResponseSchema>;
export type ReadingStateResponse = z.infer<typeof ReadingStateResponseSchema>;
export type BookshelfVisibilityItem = z.infer<typeof BookshelfVisibilityItemSchema>;
export type BookshelfVisibilityRequest = z.infer<typeof BookshelfVisibilityRequestSchema>;
export type BookshelfVisibilityResultItem = z.infer<typeof BookshelfVisibilityResultItemSchema>;
export type BookshelfVisibilityResponse = z.infer<typeof BookshelfVisibilityResponseSchema>;
export type BookshelfErrorCode = z.infer<typeof BookshelfErrorCodeSchema>;
export type BookshelfErrorContext = z.infer<typeof BookshelfErrorContextSchema>;
export type BookshelfErrorResponse = z.infer<typeof BookshelfErrorResponseSchema>;
export type CartType = z.infer<typeof CartTypeSchema>;
export type CartItem = z.infer<typeof CartItemSchema>;
export type CartItemsResponse = z.infer<typeof CartItemsResponseSchema>;
export type CartItemsRequest = z.infer<typeof CartItemsRequestSchema>;
export type CartCountResponse = z.infer<typeof CartCountResponseSchema>;
export type CartStateResponse = z.infer<typeof CartStateResponseSchema>;
export type CartErrorCode = z.infer<typeof CartErrorCodeSchema>;
export type CartErrorResponse = z.infer<typeof CartErrorResponseSchema>;
export type CornerPlatform = z.infer<typeof CornerPlatformSchema>;
export type CornerBlockType = z.infer<typeof CornerBlockTypeSchema>;
export type CornerGenerateType = z.infer<typeof CornerGenerateTypeSchema>;
export type CornerItemType = z.infer<typeof CornerItemTypeSchema>;
export type CornerCsvDataType = z.infer<typeof CornerCsvDataTypeSchema>;
export type CornerCsvUpload = z.infer<typeof CornerCsvUploadSchema>;
export type CornerProductItem = z.infer<typeof CornerProductItemSchema>;
export type CornerItemBase = z.infer<typeof CornerItemBaseSchema>;
export type CornerItem = z.infer<typeof CornerItemSchema>;
export type CornerResponseItem = z.infer<typeof CornerResponseItemSchema>;
export type CornerBlockBase = z.infer<typeof CornerBlockBaseSchema>;
export type CornerResponseBlock = z.infer<typeof CornerResponseBlockSchema>;
export type CornerBlock = z.infer<typeof CornerBlockSchema>;
export type Corner = z.infer<typeof CornerSchema>;
export type CornerDb = z.infer<typeof CornerDbSchema>;
export type BlockDb = z.infer<typeof BlockDbSchema>;
export type ItemDb = z.infer<typeof ItemDbSchema>;
export type CornerResponse = z.infer<typeof CornerResponseSchema>;
export type CouponGetType = z.infer<typeof CouponGetTypeSchema>;
export type CouponUseType = z.infer<typeof CouponUseTypeSchema>;
export type CouponUseTarget = z.infer<typeof CouponUseTargetSchema>;
export type CouponDiscountType = z.infer<typeof CouponDiscountTypeSchema>;
export type CouponUsableCount = z.infer<typeof CouponUsableCountSchema>;
export type CouponEntityType = z.infer<typeof CouponEntityTypeSchema>;
export type OwnedCouponStatus = z.infer<typeof OwnedCouponStatusSchema>;
export type CouponCsvUpload = z.infer<typeof CouponCsvUploadSchema>;
export type CouponDb = z.infer<typeof CouponDbSchema>;
export type CouponScopeDb = z.infer<typeof CouponScopeDbSchema>;
export type CouponDefinitionPublic = z.infer<typeof CouponDefinitionPublicSchema>;
export type ClaimableCouponItem = z.infer<typeof ClaimableCouponItemSchema>;
export type OwnedCouponItem = z.infer<typeof OwnedCouponItemSchema>;
export type UserCouponsResponse = z.infer<typeof UserCouponsResponseSchema>;
export type CouponResponse = z.infer<typeof CouponResponseSchema>;
export type ProductCouponsResponse = z.infer<typeof ProductCouponsResponseSchema>;
export type CouponClaimResponse = z.infer<typeof CouponClaimResponseSchema>;
export type CouponClaimConflictErrorCode = z.infer<typeof CouponClaimConflictErrorCodeSchema>;
export type CouponClaimConflictResponse = z.infer<typeof CouponClaimConflictResponseSchema>;
export type EngagementType = z.infer<typeof EngagementTypeSchema>;
export type EngagementCreateRequest = z.infer<typeof EngagementCreateRequestSchema>;
export type EngagementItem = z.infer<typeof EngagementItemSchema>;
export type EngagementListResponse = z.infer<typeof EngagementListResponseSchema>;
export type EngagementMutationResponse = z.infer<typeof EngagementMutationResponseSchema>;
export type EngagementBulkDeleteRequest = z.infer<typeof EngagementBulkDeleteRequestSchema>;
export type EngagementStatus = z.infer<typeof EngagementStatusSchema>;
export type EngagementStatusResponse = z.infer<typeof EngagementStatusResponseSchema>;
export type EngagementErrorCode = z.infer<typeof EngagementErrorCodeSchema>;
export type EngagementErrorResponse = z.infer<typeof EngagementErrorResponseSchema>;
export type IncentiveType = z.infer<typeof IncentiveTypeSchema>;
export type IncentiveValueType = z.infer<typeof IncentiveValueTypeSchema>;
export type IncentivePurchaseType = z.infer<typeof IncentivePurchaseTypeSchema>;
export type IncentivePlatform = z.infer<typeof IncentivePlatformSchema>;
export type IncentiveCsvUpload = z.infer<typeof IncentiveCsvUploadSchema>;
export type IncentiveMasterDb = z.infer<typeof IncentiveMasterDbSchema>;
export type UserIncentiveItem = z.infer<typeof UserIncentiveItemSchema>;
export type UserIncentivesResponse = z.infer<typeof UserIncentivesResponseSchema>;
export type IncentiveErrorCode = z.infer<typeof IncentiveErrorCodeSchema>;
export type IncentiveErrorResponse = z.infer<typeof IncentiveErrorResponseSchema>;
export type IncentiveUnauthorizedResponse = z.infer<typeof IncentiveUnauthorizedResponseSchema>;
export type SpecialType = z.infer<typeof SpecialTypeSchema>;
export type SpecialColorCode = z.infer<typeof SpecialColorCodeSchema>;
export type SpecialColors = z.infer<typeof SpecialColorsSchema>;
export type SpecialBlockType = z.infer<typeof SpecialBlockTypeSchema>;
export type SpecialBlockBase = z.infer<typeof SpecialBlockBaseSchema>;
export type SpecialItemType = z.infer<typeof SpecialItemTypeSchema>;
export type SpecialItemBase = z.infer<typeof SpecialItemBaseSchema>;
export type SpecialProductItem = z.infer<typeof SpecialProductItemSchema>;
export type SpecialResponseItem = z.infer<typeof SpecialResponseItemSchema>;
export type SpecialResponseBlock = z.infer<typeof SpecialResponseBlockSchema>;
export type IncentivePageResponse = z.infer<typeof IncentivePageResponseSchema>;
export type InformationName = z.infer<typeof InformationNameSchema>;
export type InformationPlatform = z.infer<typeof InformationPlatformSchema>;
export type InformationStatus = z.infer<typeof InformationStatusSchema>;
export type InformationBody = z.infer<typeof InformationBodySchema>;
export type InformationCsvUpload = z.infer<typeof InformationCsvUploadSchema>;
export type InformationDb = z.infer<typeof InformationDbSchema>;
export type InformationListItem = z.infer<typeof InformationListItemSchema>;
export type InformationListResponse = z.infer<typeof InformationListResponseSchema>;
export type InformationResponse = z.infer<typeof InformationResponseSchema>;
export type AdminInformationItem = z.infer<typeof AdminInformationItemSchema>;
export type AdminInformationListResponse = z.infer<typeof AdminInformationListResponseSchema>;
export type LotoRateName = z.infer<typeof LotoRateNameSchema>;
export type LotoStatusReason = z.infer<typeof LotoStatusReasonSchema>;
export type LotoRate = z.infer<typeof LotoRateSchema>;
export type LotoConfigBase = z.infer<typeof LotoConfigBaseSchema>;
export type LotoConfigCsvUpload = z.infer<typeof LotoConfigCsvUploadSchema>;
export type LotoConfigDb = z.infer<typeof LotoConfigDbSchema>;
export type LotoResultDb = z.infer<typeof LotoResultDbSchema>;
export type LotoDrawRequest = z.infer<typeof LotoDrawRequestSchema>;
export type LotoStatusResponse = z.infer<typeof LotoStatusResponseSchema>;
export type LotoDrawResponse = z.infer<typeof LotoDrawResponseSchema>;
export type LotoErrorCode = z.infer<typeof LotoErrorCodeSchema>;
export type LotoErrorResponse = z.infer<typeof LotoErrorResponseSchema>;
export type MenuItemViewType = z.infer<typeof MenuItemViewTypeSchema>;
export type MenuBlockViewType = z.infer<typeof MenuBlockViewTypeSchema>;
export type MenuPlatform = z.infer<typeof MenuPlatformSchema>;
export type MenuItemBase = z.infer<typeof MenuItemBaseSchema>;
export type MenuItem = z.infer<typeof MenuItemSchema>;
export type MenuBlockBase = z.infer<typeof MenuBlockBaseSchema>;
export type MenuResponseBlock = z.infer<typeof MenuResponseBlockSchema>;
export type MenuBlock = z.infer<typeof MenuBlockSchema>;
export type MenuSet = z.infer<typeof MenuSetSchema>;
export type MenuResponse = z.infer<typeof MenuResponseSchema>;
export type PaymentOrderId = z.infer<typeof PaymentOrderIdSchema>;
export type PaymentPurpose = z.infer<typeof PaymentPurposeSchema>;
export type PaymentOrderProviderId = z.infer<typeof PaymentOrderProviderIdSchema>;
export type PaymentCurrency = z.infer<typeof PaymentCurrencySchema>;
export type PaymentOrderStatus = z.infer<typeof PaymentOrderStatusSchema>;
export type PaymentFlowType = z.infer<typeof PaymentFlowTypeSchema>;
export type PaymentButtonKind = z.infer<typeof PaymentButtonKindSchema>;
export type PaymentChargeType = z.infer<typeof PaymentChargeTypeSchema>;
export type CardBrand = z.infer<typeof CardBrandSchema>;
export type PaymentProductLine = z.infer<typeof PaymentProductLineSchema>;
export type PaymentOrderSummary = z.infer<typeof PaymentOrderSummarySchema>;
export type PaymentDiscountType = z.infer<typeof PaymentDiscountTypeSchema>;
export type PaymentDiscount = z.infer<typeof PaymentDiscountSchema>;
export type PointPlanName = z.infer<typeof PointPlanNameSchema>;
export type PointItemName = z.infer<typeof PointItemNameSchema>;
export type PaymentCoinPlan = z.infer<typeof PaymentCoinPlanSchema>;
export type PaymentCoinBackBasis = z.infer<typeof PaymentCoinBackBasisSchema>;
export type PaymentCoinBack = z.infer<typeof PaymentCoinBackSchema>;
export type PaymentCoinUsageLine = z.infer<typeof PaymentCoinUsageLineSchema>;
export type PaymentCoinUsage = z.infer<typeof PaymentCoinUsageSchema>;
export type PaymentCardSummary = z.infer<typeof PaymentCardSummarySchema>;
export type PaymentStoredCard = z.infer<typeof PaymentStoredCardSchema>;
export type PaymentButtonConfig = z.infer<typeof PaymentButtonConfigSchema>;
export type PaymentRenderDirective = z.infer<typeof PaymentRenderDirectiveSchema>;
export type PaymentNextActionType = z.infer<typeof PaymentNextActionTypeSchema>;
export type PaymentNextActionMethod = z.infer<typeof PaymentNextActionMethodSchema>;
export type PaymentNextAction = z.infer<typeof PaymentNextActionSchema>;
export type PaymentFailure = z.infer<typeof PaymentFailureSchema>;
export type PaymentProviderId = z.infer<typeof PaymentProviderIdSchema>;
export type PaymentCheckoutRequest = z.infer<typeof PaymentCheckoutRequestSchema>;
export type PaymentCreditInput = z.infer<typeof PaymentCreditInputSchema>;
export type PaymentIntentRequest = z.infer<typeof PaymentIntentRequestSchema>;
export type PaymentCheckoutResponse = z.infer<typeof PaymentCheckoutResponseSchema>;
export type PaymentGrantResult = z.infer<typeof PaymentGrantResultSchema>;
export type PaymentIntentResponse = z.infer<typeof PaymentIntentResponseSchema>;
export type PaymentOrderResponse = z.infer<typeof PaymentOrderResponseSchema>;
export type PaymentOrderListProduct = z.infer<typeof PaymentOrderListProductSchema>;
export type PaymentReceipt = z.infer<typeof PaymentReceiptSchema>;
export type PaymentOrderListItem = z.infer<typeof PaymentOrderListItemSchema>;
export type PaymentOrderListResponse = z.infer<typeof PaymentOrderListResponseSchema>;
export type PaymentErrorCode = z.infer<typeof PaymentErrorCodeSchema>;
export type PaymentErrorContext = z.infer<typeof PaymentErrorContextSchema>;
export type PaymentErrorResponse = z.infer<typeof PaymentErrorResponseSchema>;
export type PaymentUnauthorizedResponse = z.infer<typeof PaymentUnauthorizedResponseSchema>;
export type CheckoutPurpose = z.infer<typeof CheckoutPurposeSchema>;
export type PaymentProviderStatus = z.infer<typeof PaymentProviderStatusSchema>;
export type PaymentProviderPlatformDetail = z.infer<typeof PaymentProviderPlatformDetailSchema>;
export type ProviderUnselectableReason = z.infer<typeof ProviderUnselectableReasonSchema>;
export type PaymentProviderDb = z.infer<typeof PaymentProviderDbSchema>;
export type RegisteredCard = z.infer<typeof RegisteredCardSchema>;
export type PaymentProviderPublic = z.infer<typeof PaymentProviderPublicSchema>;
export type CoinCheckoutItem = z.infer<typeof CoinCheckoutItemSchema>;
export type CoinBalanceSummary = z.infer<typeof CoinBalanceSummarySchema>;
export type CoinCheckoutErrorCode = z.infer<typeof CoinCheckoutErrorCodeSchema>;
export type CoinCheckoutErrorResponse = z.infer<typeof CoinCheckoutErrorResponseSchema>;
export type CoinCheckoutUnauthorizedResponse = z.infer<typeof CoinCheckoutUnauthorizedResponseSchema>;
export type CheckoutSource = z.infer<typeof CheckoutSourceSchema>;
export type ExclusionReason = z.infer<typeof ExclusionReasonSchema>;
export type CouponUnusableReason = z.infer<typeof CouponUnusableReasonSchema>;
export type BookCheckoutItem = z.infer<typeof BookCheckoutItemSchema>;
export type ExcludedItem = z.infer<typeof ExcludedItemSchema>;
export type CheckoutSummary = z.infer<typeof CheckoutSummarySchema>;
export type CheckoutCoupon = z.infer<typeof CheckoutCouponSchema>;
export type CheckoutItemType = z.infer<typeof CheckoutItemTypeSchema>;
export type CoinBackBasis = z.infer<typeof CoinBackBasisSchema>;
export type CoinBack = z.infer<typeof CoinBackSchema>;
export type BookCheckoutResponse = z.infer<typeof BookCheckoutResponseSchema>;
export type CoinCheckoutResponse = z.infer<typeof CoinCheckoutResponseSchema>;
export type CheckoutResponse = z.infer<typeof CheckoutResponseSchema>;
export type CouponApplyRequest = z.infer<typeof CouponApplyRequestSchema>;
export type CouponApplyResponse = z.infer<typeof CouponApplyResponseSchema>;
export type CheckoutErrorCode = z.infer<typeof CheckoutErrorCodeSchema>;
export type CheckoutErrorResponse = z.infer<typeof CheckoutErrorResponseSchema>;
export type CheckoutUnauthorizedResponse = z.infer<typeof CheckoutUnauthorizedResponseSchema>;
export type PointPlanEntityType = z.infer<typeof PointPlanEntityTypeSchema>;
export type PointPlanUploadItem = z.infer<typeof PointPlanUploadItemSchema>;
export type PointPlanUpload = z.infer<typeof PointPlanUploadSchema>;
export type PointPlanDb = z.infer<typeof PointPlanDbSchema>;
export type PointPlanItemDb = z.infer<typeof PointPlanItemDbSchema>;
export type PointPlanItemPublic = z.infer<typeof PointPlanItemPublicSchema>;
export type PointPlanItemsResponse = z.infer<typeof PointPlanItemsResponseSchema>;
export type EcoinBalanceResponse = z.infer<typeof EcoinBalanceResponseSchema>;
export type EcoinHistoryTxType = z.infer<typeof EcoinHistoryTxTypeSchema>;
export type EcoinCoinType = z.infer<typeof EcoinCoinTypeSchema>;
export type EcoinHistoryItem = z.infer<typeof EcoinHistoryItemSchema>;
export type EcoinHistoryResponse = z.infer<typeof EcoinHistoryResponseSchema>;
export type ProductBase = z.infer<typeof ProductBaseSchema>;
export type ProductCsvUpload = z.infer<typeof ProductCsvUploadSchema>;
export type ProductDb = z.infer<typeof ProductDbSchema>;
export type ProductResponse = z.infer<typeof ProductResponseSchema>;
export type ReservationCreateRequest = z.infer<typeof ReservationCreateRequestSchema>;
export type ReservationItem = z.infer<typeof ReservationItemSchema>;
export type ReservationListResponse = z.infer<typeof ReservationListResponseSchema>;
export type ReservationCreateResponse = z.infer<typeof ReservationCreateResponseSchema>;
export type ReservationBulkDeleteRequest = z.infer<typeof ReservationBulkDeleteRequestSchema>;
export type ReservationStatus = z.infer<typeof ReservationStatusSchema>;
export type ReservationStatusResponse = z.infer<typeof ReservationStatusResponseSchema>;
export type ReservationErrorCode = z.infer<typeof ReservationErrorCodeSchema>;
export type ReservationErrorResponse = z.infer<typeof ReservationErrorResponseSchema>;
export type ReservationRecordStatus = z.infer<typeof ReservationRecordStatusSchema>;
export type ReservationCancelDigestStatus = z.infer<typeof ReservationCancelDigestStatusSchema>;
export type ReservationCancelReason = z.infer<typeof ReservationCancelReasonSchema>;
export type ReservationDb = z.infer<typeof ReservationDbSchema>;
export type ReservationCancelEntry = z.infer<typeof ReservationCancelEntrySchema>;
export type ReservationCancelDigestDb = z.infer<typeof ReservationCancelDigestDbSchema>;
export type AlgoliaFacetFilters = z.infer<typeof AlgoliaFacetFiltersSchema>;
export type AlgoliaSuggestRequest = z.infer<typeof AlgoliaSuggestRequestSchema>;
export type AlgoliaAuthorSuggestRequest = z.infer<typeof AlgoliaAuthorSuggestRequestSchema>;
export type AlgoliaSearchRequest = z.infer<typeof AlgoliaSearchRequestSchema>;
export type SearchResultHit = z.infer<typeof SearchResultHitSchema>;
export type HighlightMatch = z.infer<typeof HighlightMatchSchema>;
export type TitleSuggestHit = z.infer<typeof TitleSuggestHitSchema>;
export type AuthorSuggestHit = z.infer<typeof AuthorSuggestHitSchema>;
export type CartGroupItem = z.infer<typeof CartGroupItemSchema>;
export type CartGroupResponse = z.infer<typeof CartGroupResponseSchema>;
export type CartGroupErrorCode = z.infer<typeof CartGroupErrorCodeSchema>;
export type CartGroupErrorResponse = z.infer<typeof CartGroupErrorResponseSchema>;
export type SpecialPlatform = z.infer<typeof SpecialPlatformSchema>;
export type SeriesName = z.infer<typeof SeriesNameSchema>;
export type SpecialPageStatus = z.infer<typeof SpecialPageStatusSchema>;
export type SpecialCsvDataType = z.infer<typeof SpecialCsvDataTypeSchema>;
export type SpecialCsvUpload = z.infer<typeof SpecialCsvUploadSchema>;
export type SpecialItem = z.infer<typeof SpecialItemSchema>;
export type SpecialBlock = z.infer<typeof SpecialBlockSchema>;
export type Special = z.infer<typeof SpecialSchema>;
export type SpecialResponse = z.infer<typeof SpecialResponseSchema>;
export type SpecialListItem = z.infer<typeof SpecialListItemSchema>;
export type CampaignListItem = z.infer<typeof CampaignListItemSchema>;
export type SpecialListResponse = z.infer<typeof SpecialListResponseSchema>;
export type CampaignListResponse = z.infer<typeof CampaignListResponseSchema>;
export type MagazineCatchUpdateRequest = z.infer<typeof MagazineCatchUpdateRequestSchema>;
export type MagazineCatchUpdateResponse = z.infer<typeof MagazineCatchUpdateResponseSchema>;
export type KanaGroup = z.infer<typeof KanaGroupSchema>;
export type AuthorEntry = z.infer<typeof AuthorEntrySchema>;
export type AuthorGroup = z.infer<typeof AuthorGroupSchema>;
export type AuthorListResponse = z.infer<typeof AuthorListResponseSchema>;
export type CategoryItem = z.infer<typeof CategoryItemSchema>;
export type CategoryListResponse = z.infer<typeof CategoryListResponseSchema>;
export type Decade = z.infer<typeof DecadeSchema>;
export type BookGroupEntry = z.infer<typeof BookGroupEntrySchema>;
export type BookGroup = z.infer<typeof BookGroupSchema>;
export type BookGroupListResponse = z.infer<typeof BookGroupListResponseSchema>;
export type BookGroupItem = z.infer<typeof BookGroupItemSchema>;
export type BookGroupSaved = z.infer<typeof BookGroupSavedSchema>;
export type MagazineListItem = z.infer<typeof MagazineListItemSchema>;
export type MagazineListResponse = z.infer<typeof MagazineListResponseSchema>;
export type ProductDetail = z.infer<typeof ProductDetailSchema>;
export type SpecialProduct = z.infer<typeof SpecialProductSchema>;
export type ProductGroup = z.infer<typeof ProductGroupSchema>;
export type ProductGroupAuthor = z.infer<typeof ProductGroupAuthorSchema>;
export type CheckedProduct = z.infer<typeof CheckedProductSchema>;
export type ProductDetailPageResponse = z.infer<typeof ProductDetailPageResponseSchema>;
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;
export type ProductListName = z.infer<typeof ProductListNameSchema>;
export type ProductListResponse = z.infer<typeof ProductListResponseSchema>;
export type SearchKeyResponse = z.infer<typeof SearchKeyResponseSchema>;
